import { getStaffServiceIds } from "@/lib/staff-associations";
import { useState, useEffect, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import DashboardLayout from "@/components/DashboardLayout";
import CalendarView from "@/components/CalendarView";
import AnalyticsView from "@/components/AnalyticsView";
import EmailTemplatesManager from "@/components/EmailTemplatesManager";
import DiscountCodesManager from "@/components/DiscountCodesManager";
import ApiKeysManager from "@/components/ApiKeysManager";
import BlockedDatesManager from "@/components/BlockedDatesManager";
import StaffAbsencesManager from "@/components/StaffAbsencesManager";
import CustomersManager from "@/components/CustomersManager";
import ImageUpload from "@/components/ImageUpload";
import BookingsListView from "@/components/BookingsListView";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { 
  Building2, Calendar, Euro, Settings, Users, Package, 
  Clock, TrendingUp, Edit, Save, Plus, Grid3X3, ExternalLink,
  Scissors, UserCircle, CalendarDays, BookOpen, Palette, Link2, Unlink, Loader2,
  CreditCard, CheckCircle, XCircle, Crown, Trash2, Pencil, Eye, Phone, Mail, FileText, BarChart3, Globe, X,
  GripVertical, Copy, Check, ImageIcon, Bell, Send, ToggleLeft, ToggleRight, ClipboardList, MapPin, Tag, Key,
  ChevronUp, ChevronDown, MessageCircle
} from "lucide-react";
import { DndContext, closestCenter, PointerSensor, TouchSensor, useSensor, useSensors, DragOverlay } from "@dnd-kit/core";
import { SortableContext, useSortable, rectSortingStrategy, arrayMove } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import axios from "axios";
import { toast } from "sonner";
import { API } from "@/App";
import { format, parseISO } from "date-fns";
import { it } from "date-fns/locale";

// Extracted StatCard component to avoid unstable nested component warning
const BusinessStatCard = ({ title, value, icon: Icon, color, onClick }) => (
  <Card className={`hover:shadow-lg transition-shadow ${onClick ? 'cursor-pointer' : ''}`} onClick={onClick}>
    <CardContent className="p-4 sm:pt-6">
      <div className="flex items-start justify-between">
        <div className="min-w-0 flex-1">
          <p className="text-xs sm:text-sm text-slate-500 truncate">{title}</p>
          <p className="text-xl sm:text-3xl font-bold mt-1 truncate" style={{ color }}>{value}</p>
        </div>
        <div className="p-2 sm:p-3 rounded-lg flex-shrink-0 ml-2" style={{ backgroundColor: `${color}15` }}>
          <Icon className="h-5 w-5 sm:h-6 sm:w-6" style={{ color }} />
        </div>
      </div>
    </CardContent>
  </Card>
);

// Sortable wrapper for service cards
const SortableServiceCard = ({ id, children }) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.4 : 1,
    zIndex: isDragging ? 50 : 'auto',
  };
  return (
    <div ref={setNodeRef} style={style} {...attributes}>
      {children({ listeners, isDragging })}
    </div>
  );
};

// Sortable wrapper for category cards
const SortableCategoryCard = ({ id, children }) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.4 : 1,
    zIndex: isDragging ? 50 : 'auto',
  };
  return (
    <div ref={setNodeRef} style={style} {...attributes}>
      {children({ listeners, isDragging })}
    </div>
  );
};

const BusinessOwnerDashboard = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [business, setBusiness] = useState(null);
  const [services, setServices] = useState([]);
  const [staff, setStaff] = useState([]);
  const [calendars, setCalendars] = useState([]);
  const [categories, setCategories] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [editingBusiness, setEditingBusiness] = useState(false);
  const [businessForm, setBusinessForm] = useState({});
  
  // Read initial tab from URL query param
  const tabFromUrl = searchParams.get('tab');
  const [activeTab, setActiveTab] = useState(tabFromUrl || "agenda");
  
  // Update activeTab when URL changes
  useEffect(() => {
    const newTab = searchParams.get('tab');
    if (newTab && newTab !== activeTab) {
      setActiveTab(newTab);
    }
  }, [searchParams]);
  
  const [gcalStatus, setGcalStatus] = useState({ connected: false, email: null, configured: false, selected_calendar_id: null, selected_calendar_name: null });
  const [connectingGcal, setConnectingGcal] = useState(false);
  const [googleCalendars, setGoogleCalendars] = useState([]);
  const [loadingGoogleCalendars, setLoadingGoogleCalendars] = useState(false);
  
  // Stripe Connect state
  const [stripeConnectStatus, setStripeConnectStatus] = useState({ connected: false, platform_configured: false });
  const [connectingStripe, setConnectingStripe] = useState(false);
  
  // Subscription state
  const [subscription, setSubscription] = useState(null);
  const [subscriptionPlans, setSubscriptionPlans] = useState([]);
  const [subscribing, setSubscribing] = useState(false);
  
  // Settings sub-tab state
  const [settingsSubTab, setSettingsSubTab] = useState("general");
  
  // Firebase Push Notifications state
  const [firebaseServerKey, setFirebaseServerKey] = useState("");
  const [savingFirebaseKey, setSavingFirebaseKey] = useState(false);
  const [testingPushNotification, setTestingPushNotification] = useState(false);
  
  // SSE connection for real-time updates
  const [sseConnected, setSseConnected] = useState(false);
  const [newBookingHighlight, setNewBookingHighlight] = useState(null);

  // Service dialog state
  const [serviceDialogOpen, setServiceDialogOpen] = useState(false);
  const [editingService, setEditingService] = useState(null);
  const [serviceForm, setServiceForm] = useState({
    name: "", description: "", image_url: "", category_id: "",
    duration_minutes: 30, price: 0, currency: "EUR",
    session_type: "single", num_sessions: 1, session_interval_days: 7
  });

  // Staff dialog state
  const [staffDialogOpen, setStaffDialogOpen] = useState(false);
  const [editingStaff, setEditingStaff] = useState(null);
  const [staffForm, setStaffForm] = useState({
    name: "", email: "", phone: "", image_url: "", role: "employee", service_ids: [], is_active: true,
    color: "",
    // Staff login
    has_login: false,
    password: "",
    // Staff permissions
    can_edit_insert: true,
    can_delete: false,
    can_view_all_staff: false
  });

  // Service filter state
  const [serviceCategoryFilter, setServiceCategoryFilter] = useState("all");

  // Category dialog state
  const [categoryDialogOpen, setCategoryDialogOpen] = useState(false);
  const [editingCategory, setEditingCategory] = useState(null);
  const [categoryForm, setCategoryForm] = useState({
    name: "", color: "#4F46E5", order: 0
  });

  // Calendar dialog state
  const [calendarDialogOpen, setCalendarDialogOpen] = useState(false);
  const [editingCalendar, setEditingCalendar] = useState(null);
  const [calendarForm, setCalendarForm] = useState({
    name: "", description: "", service_ids: [], staff_id: "",
    slot_duration_minutes: 30, buffer_before: 0, buffer_after: 0, max_days_advance: 30,
    default_availability: []
  });

  // Booking detail dialog state
  const [bookingDetailOpen, setBookingDetailOpen] = useState(false);
  const [selectedBooking, setSelectedBooking] = useState(null);
  const [editingBooking, setEditingBooking] = useState(false);
  const [bookingEditForm, setBookingEditForm] = useState({
    customer_name: "", customer_email: "", customer_phone: "",
    status: "confirmed", notes: "", start_datetime: "", start_time: ""
  });

  // Delete confirmation dialog state
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState({ type: "", id: "", name: "" });
  const [deleting, setDeleting] = useState(false);

  // DnD sensors — require 8px move before drag starts (prevents accidental drags on tap/click)
  const dndSensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 200, tolerance: 5 } })
  );

  // DnD: reorder services on drag end
  const handleServiceDragEnd = useCallback(async (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    // Build the currently visible sorted list
    const filtered = services
      .filter(s => {
        if (serviceCategoryFilter === "all") return true;
        if (serviceCategoryFilter === "none") return !s.category_id;
        return s.category_id === serviceCategoryFilter;
      })
      .sort((a, b) => (a.order || 0) - (b.order || 0));

    const oldIndex = filtered.findIndex(s => s.service_id === active.id);
    const newIndex = filtered.findIndex(s => s.service_id === over.id);
    if (oldIndex === -1 || newIndex === -1) return;

    const reordered = arrayMove(filtered, oldIndex, newIndex);

    // Optimistic UI update
    const orderMap = new Map();
    reordered.forEach((s, i) => orderMap.set(s.service_id, i));
    setServices(prev => prev.map(s => orderMap.has(s.service_id) ? { ...s, order: orderMap.get(s.service_id) } : s));

    // Build full list for API: reordered filtered items + remaining untouched items
    const reorderedIds = reordered.map(s => s.service_id);
    const otherIds = services
      .filter(s => !reorderedIds.includes(s.service_id))
      .sort((a, b) => (a.order || 0) - (b.order || 0))
      .map(s => s.service_id);

    try {
      const token = localStorage.getItem("token");
      await axios.put(`${API}/my-business/services/reorder`, { service_ids: [...reorderedIds, ...otherIds] }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Ordine servizi aggiornato");
      fetchData();
    } catch {
      toast.error("Errore nel riordino dei servizi");
      fetchData();
    }
  }, [services, serviceCategoryFilter]);

  // DnD: reorder categories on drag end
  const handleCategoryDragEnd = useCallback(async (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    const sorted = [...categories].sort((a, b) => (a.order || 0) - (b.order || 0));
    const oldIndex = sorted.findIndex(c => c.category_id === active.id);
    const newIndex = sorted.findIndex(c => c.category_id === over.id);
    if (oldIndex === -1 || newIndex === -1) return;

    const reordered = arrayMove(sorted, oldIndex, newIndex);

    // Optimistic UI
    setCategories(reordered.map((c, i) => ({ ...c, order: i })));

    try {
      const token = localStorage.getItem("token");
      await axios.put(`${API}/my-business/categories/reorder`, {
        category_ids: reordered.map(c => c.category_id)
      }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success("Ordine categorie aggiornato");
      fetchData();
    } catch {
      toast.error("Errore nel riordino delle categorie");
      fetchData();
    }
  }, [categories]);

  useEffect(() => {
    fetchData();
    // Check for callbacks
    if (searchParams.get("gcal") === "connected") {
      toast.success("Google Calendar collegato con successo!");
    }
    if (searchParams.get("stripe_connected") === "true") {
      toast.success("Stripe collegato con successo!");
    }
    if (searchParams.get("stripe_error")) {
      toast.error(`Errore Stripe: ${searchParams.get("stripe_error")}`);
    }
  }, [searchParams]);

  // SSE connection for real-time booking updates with polling fallback
  useEffect(() => {
    let eventSource = null;
    let pollingInterval = null;
    let knownBookingIds = new Set();
    let sseWorking = false;
    let isFirstPoll = true;
    
    // Polling function - always runs as backup
    const startPolling = () => {
      if (pollingInterval) return; // Already polling
      
      const token = localStorage.getItem("token");
      if (!token) return;
      
      console.log("Starting polling for real-time updates (every 10s)");
      
      pollingInterval = setInterval(async () => {
        try {
          const response = await axios.get(`${API}/my-business/bookings`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          
          const newBookings = response.data;
          
          if (isFirstPoll) {
            // First poll: just store the IDs, don't notify
            newBookings.forEach(b => knownBookingIds.add(b.booking_id));
            isFirstPoll = false;
            console.log(`Polling initialized with ${knownBookingIds.size} existing bookings`);
          } else {
            // Check for new bookings by ID
            const newOnes = newBookings.filter(b => !knownBookingIds.has(b.booking_id));
            
            if (newOnes.length > 0) {
              console.log(`Found ${newOnes.length} new booking(s) via polling`);
              
              newOnes.forEach(booking => {
                knownBookingIds.add(booking.booking_id);
                
                // Show toast notification
                toast.success(
                  <div className="flex flex-col">
                    <span className="font-semibold">Nuova prenotazione!</span>
                    <span className="text-sm">{booking.customer_name}</span>
                  </div>,
                  { duration: 5000 }
                );
                
                // Highlight the new booking
                setNewBookingHighlight(booking.booking_id);
                setTimeout(() => setNewBookingHighlight(null), 10000);
              });
              
              // Update bookings state
              setBookings(newBookings);
            }
          }
        } catch (e) {
          console.log("Polling error:", e);
        }
      }, 10000); // Poll every 10 seconds
    };
    
    const connectSSE = () => {
      const token = localStorage.getItem("token");
      if (!token) return;
      
      try {
        // EventSource doesn't support headers, so we use URL parameter
        eventSource = new EventSource(`${API}/my-business/bookings/stream?token=${token}`);
        
        eventSource.onopen = () => {
          sseWorking = true;
          setSseConnected(true);
          console.log("SSE connected for real-time updates");
        };
        
        eventSource.onmessage = (event) => {
          try {
            const data = JSON.parse(event.data);
            console.log("SSE message received:", data.type);
            
            if (data.type === "new_booking") {
              console.log("New booking via SSE!", data.booking.customer_name);
              
              // Add to known IDs so polling doesn't duplicate
              knownBookingIds.add(data.booking.booking_id);
              
              // Add new booking to state
              setBookings(prev => [data.booking, ...prev]);
              
              // Highlight the new booking
              setNewBookingHighlight(data.booking.booking_id);
              
              // Show toast notification
              toast.success(
                <div className="flex flex-col">
                  <span className="font-semibold">Nuova prenotazione!</span>
                  <span className="text-sm">{data.booking.customer_name}</span>
                </div>,
                { duration: 5000 }
              );
              
              // Remove highlight after 10 seconds
              setTimeout(() => setNewBookingHighlight(null), 10000);
            }
          } catch (e) {
            console.log("SSE parse error:", e);
          }
        };
        
        eventSource.onerror = (e) => {
          console.log("SSE error:", e);
          sseWorking = false;
          setSseConnected(false);
          eventSource?.close();
          eventSource = null;
        };
      } catch (e) {
        console.log("SSE connection failed:", e);
      }
    };
    
    // Always start polling as guaranteed backup
    startPolling();
    
    // Also try SSE for instant updates (if it works, great - if not, polling handles it)
    connectSSE();
    
    return () => {
      eventSource?.close();
      if (pollingInterval) clearInterval(pollingInterval);
    };
  }, []);

  const fetchData = async () => {
    try {
      const token = localStorage.getItem("token");
      const headers = { Authorization: `Bearer ${token}` };
      
      const [bizRes, servicesRes, staffRes, calendarsRes, categoriesRes, bookingsRes, plansRes] = await Promise.all([
        axios.get(`${API}/my-business`, { headers }),
        axios.get(`${API}/my-business/services`, { headers }),
        axios.get(`${API}/my-business/staff`, { headers }),
        axios.get(`${API}/my-business/calendars`, { headers }),
        axios.get(`${API}/my-business/categories`, { headers }),
        axios.get(`${API}/my-business/bookings`, { headers }),
        axios.get(`${API}/public/subscription-plans`).catch(() => ({ data: [] }))
      ]);
      
      setBusiness(bizRes.data);
      setBusinessForm(bizRes.data);
      setServices(servicesRes.data);
      setStaff(staffRes.data);
      setCalendars(calendarsRes.data);
      setCategories(categoriesRes.data);
      setBookings(bookingsRes.data);
      setSubscriptionPlans(plansRes.data);
      
      // Load Firebase Service Account if exists
      if (bizRes.data?.firebase_service_account) {
        setFirebaseServerKey(bizRes.data.firebase_service_account);
      }
      
      // Fetch Google Calendar status
      if (bizRes.data?.business_id) {
        try {
          const gcalRes = await axios.get(`${API}/business/${bizRes.data.business_id}/google-calendar/status`, { headers });
          setGcalStatus(gcalRes.data);
        } catch (e) {
          console.log("Google Calendar status not available");
        }
        
        // Fetch Stripe Connect status
        try {
          const stripeRes = await axios.get(`${API}/stripe-connect/status/${bizRes.data.business_id}`, { headers });
          setStripeConnectStatus(stripeRes.data);
        } catch (e) {
          console.log("Stripe Connect status not available");
        }
        
        // Fetch subscription status
        try {
          const subRes = await axios.get(`${API}/my-business/subscription`, { headers });
          setSubscription(subRes.data);
        } catch (e) {
          console.log("Subscription status not available");
        }
      }
    } catch (error) {
      console.error("Error fetching data:", error);
      if (error.response?.status === 403 || error.response?.status === 404) {
        toast.error("Accesso non autorizzato o nessuna azienda associata");
        navigate("/login");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleConnectGoogleCalendar = async () => {
    if (!business?.business_id) return;
    setConnectingGcal(true);
    try {
      const token = localStorage.getItem("token");
      const res = await axios.get(`${API}/business/${business.business_id}/google-calendar/auth-url`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      window.location.href = res.data.authorization_url;
    } catch (error) {
      toast.error(error.response?.data?.detail || "Errore nella connessione a Google Calendar");
      setConnectingGcal(false);
    }
  };

  const handleDisconnectGoogleCalendar = async () => {
    if (!business?.business_id) return;
    try {
      const token = localStorage.getItem("token");
      await axios.post(`${API}/business/${business.business_id}/google-calendar/disconnect`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setGcalStatus({ connected: false, email: null, configured: gcalStatus.configured });
      setGoogleCalendars([]);
      toast.success("Google Calendar disconnesso");
    } catch (error) {
      toast.error("Errore nella disconnessione");
    }
  };

  // Fetch Google Calendars list
  const fetchGoogleCalendars = async () => {
    if (!business?.business_id || !gcalStatus.connected) return;
    setLoadingGoogleCalendars(true);
    try {
      const token = localStorage.getItem("token");
      const res = await axios.get(`${API}/business/${business.business_id}/google-calendar/calendars`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setGoogleCalendars(res.data.calendars || []);
    } catch (error) {
      console.error("Error fetching Google calendars:", error);
    } finally {
      setLoadingGoogleCalendars(false);
    }
  };

  // Select a Google Calendar
  const handleSelectGoogleCalendar = async (calendarId, calendarName) => {
    if (!business?.business_id) return;
    try {
      const token = localStorage.getItem("token");
      await axios.post(`${API}/business/${business.business_id}/google-calendar/select`, {
        calendar_id: calendarId,
        calendar_name: calendarName
      }, { headers: { Authorization: `Bearer ${token}` } });
      setGcalStatus(prev => ({ ...prev, selected_calendar_id: calendarId, selected_calendar_name: calendarName }));
      toast.success(`Calendario "${calendarName}" selezionato`);
    } catch (error) {
      toast.error("Errore nella selezione del calendario");
    }
  };

  // Fetch Google Calendars when connected
  useEffect(() => {
    if (gcalStatus.connected && business?.business_id) {
      fetchGoogleCalendars();
    }
  }, [gcalStatus.connected, business?.business_id]);

  // Stripe Connect handlers
  const handleConnectStripe = async () => {
    if (!business?.business_id) return;
    setConnectingStripe(true);
    try {
      const token = localStorage.getItem("token");
      const res = await axios.get(`${API}/stripe-connect/auth/${business.business_id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      window.location.href = res.data.auth_url;
    } catch (error) {
      toast.error(error.response?.data?.detail || "Errore nella connessione a Stripe");
      setConnectingStripe(false);
    }
  };

  const handleDisconnectStripe = async () => {
    if (!business?.business_id) return;
    if (!confirm("Sei sicuro di voler disconnettere Stripe?")) return;
    
    try {
      const token = localStorage.getItem("token");
      await axios.post(`${API}/stripe-connect/disconnect/${business.business_id}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setStripeConnectStatus({ connected: false, platform_configured: stripeConnectStatus.platform_configured });
      toast.success("Stripe disconnesso");
    } catch (error) {
      toast.error("Errore nella disconnessione");
    }
  };

  // Subscription handlers
  const handleSubscribe = async (planId) => {
    setSubscribing(true);
    try {
      const token = localStorage.getItem("token");
      const res = await axios.post(`${API}/subscriptions/checkout?plan_id=${planId}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      window.location.href = res.data.checkout_url;
    } catch (error) {
      toast.error(error.response?.data?.detail || "Errore nella creazione del checkout");
      setSubscribing(false);
    }
  };

  const handleCancelSubscription = async () => {
    if (!confirm("Sei sicuro di voler cancellare l'abbonamento? Rimarrà attivo fino alla fine del periodo.")) return;
    
    try {
      const token = localStorage.getItem("token");
      await axios.post(`${API}/my-business/subscription/cancel`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Abbonamento cancellato. Rimarrà attivo fino alla fine del periodo.");
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Errore nella cancellazione");
    }
  };

  const handleUpdateBusiness = async () => {
    try {
      const token = localStorage.getItem("token");
      await axios.put(`${API}/my-business`, businessForm, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Informazioni azienda aggiornate");
      setEditingBusiness(false);
      fetchData();
    } catch (error) {
      toast.error("Errore nell'aggiornamento");
    }
  };

  // Toggle service active/inactive
  const handleToggleServiceActive = async (service) => {
    try {
      const token = localStorage.getItem("token");
      const newStatus = service.is_active === false ? true : false;
      await axios.put(`${API}/my-business/services/${service.service_id}`, {
        ...service,
        is_active: newStatus
      }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success(newStatus ? "Servizio attivato" : "Servizio disattivato");
      fetchData();
    } catch (error) {
      toast.error("Errore nell'aggiornamento del servizio");
    }
  };

  // ==================== COPY LINK FUNCTIONS ====================
  const [copiedCalendarId, setCopiedCalendarId] = useState(null);
  
  const copyCalendarLink = async (calendarId) => {
    const baseUrl = window.location.origin;
    const link = `${baseUrl}/book/${business?.slug}?calendar=${calendarId}`;
    try {
      await navigator.clipboard.writeText(link);
      setCopiedCalendarId(calendarId);
      toast.success("Link copiato!");
      setTimeout(() => setCopiedCalendarId(null), 2000);
    } catch (error) {
      toast.error("Errore nella copia del link");
    }
  };

  // ==================== SERVICE CRUD ====================
  const handleSaveService = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem("token");
      const headers = { Authorization: `Bearer ${token}` };
      if (editingService) {
        await axios.put(`${API}/my-business/services/${editingService.service_id}`, serviceForm, { headers });
        toast.success("Servizio aggiornato!");
      } else {
        await axios.post(`${API}/my-business/services`, serviceForm, { headers });
        toast.success("Servizio creato!");
      }
      setServiceDialogOpen(false);
      setEditingService(null);
      setServiceForm({ name: "", description: "", image_url: "", category_id: "", duration_minutes: 30, price: 0, currency: "EUR", session_type: "single", num_sessions: 1, session_interval_days: 7 });
      fetchData();
    } catch (error) {
      toast.error("Errore durante il salvataggio");
    }
  };

  const handleDeleteService = async (serviceId) => {
    const service = services.find(s => s.service_id === serviceId);
    setDeleteTarget({ type: "service", id: serviceId, name: service?.name || "Servizio" });
    setDeleteConfirmOpen(true);
  };

  const openEditService = (service) => {
    setEditingService(service);
    setServiceForm({
      name: service.name || "",
      description: service.description || "",
      image_url: service.image_url || "",
      category_id: service.category_id || "",
      duration_minutes: service.duration_minutes || 30,
      price: service.price || 0,
      currency: service.currency || "EUR",
      session_type: service.session_type || "single",
      num_sessions: service.num_sessions || 1,
      session_interval_days: service.session_interval_days || 7
    });
    setServiceDialogOpen(true);
  };

  // ==================== CATEGORY CRUD ====================
  const handleSaveCategory = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem("token");
      const headers = { Authorization: `Bearer ${token}` };
      if (editingCategory) {
        await axios.put(`${API}/my-business/categories/${editingCategory.category_id}`, categoryForm, { headers });
        toast.success("Categoria aggiornata!");
      } else {
        await axios.post(`${API}/my-business/categories`, categoryForm, { headers });
        toast.success("Categoria creata!");
      }
      setCategoryDialogOpen(false);
      setEditingCategory(null);
      setCategoryForm({ name: "", color: "#4F46E5", order: 0 });
      fetchData();
    } catch (error) {
      toast.error("Errore durante il salvataggio della categoria");
    }
  };

  const handleDeleteCategory = async (categoryId) => {
    const category = categories.find(c => c.category_id === categoryId);
    setDeleteTarget({ type: "category", id: categoryId, name: category?.name || "Categoria" });
    setDeleteConfirmOpen(true);
  };

  const openEditCategory = (category) => {
    setEditingCategory(category);
    setCategoryForm({
      name: category.name || "",
      color: category.color || "#4F46E5",
      order: category.order || 0
    });
    setCategoryDialogOpen(true);
  };

  // ==================== STAFF CRUD ====================
  const handleSaveStaff = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem("token");
      const headers = { Authorization: `Bearer ${token}` };
      if (editingStaff) {
        await axios.put(`${API}/my-business/staff/${editingStaff.staff_id}`, staffForm, { headers });
        toast.success("Staff aggiornato!");
      } else {
        await axios.post(`${API}/my-business/staff`, staffForm, { headers });
        toast.success("Staff creato!");
      }
      setStaffDialogOpen(false);
      setEditingStaff(null);
      setStaffForm({ name: "", email: "", phone: "", image_url: "", role: "employee", service_ids: [] });
      fetchData();
    } catch (error) {
      console.error("Staff save error:", error.response?.data || error.message);
      toast.error(error.response?.data?.detail || "Errore durante il salvataggio");
    }
  };

  const handleDeleteStaff = async (staffId) => {
    const member = staff.find(s => s.staff_id === staffId);
    setDeleteTarget({ type: "staff", id: staffId, name: member?.name || "Staff" });
    setDeleteConfirmOpen(true);
  };

  // Move staff up/down in order
  const moveStaff = async (staffId, direction) => {
    const sortedStaff = [...staff].sort((a, b) => (a.order || 0) - (b.order || 0));
    const currentIndex = sortedStaff.findIndex(s => s.staff_id === staffId);
    
    if (direction === "up" && currentIndex > 0) {
      const newOrder = [...sortedStaff];
      [newOrder[currentIndex - 1], newOrder[currentIndex]] = [newOrder[currentIndex], newOrder[currentIndex - 1]];
      await saveStaffOrder(newOrder.map(s => s.staff_id));
    } else if (direction === "down" && currentIndex < sortedStaff.length - 1) {
      const newOrder = [...sortedStaff];
      [newOrder[currentIndex], newOrder[currentIndex + 1]] = [newOrder[currentIndex + 1], newOrder[currentIndex]];
      await saveStaffOrder(newOrder.map(s => s.staff_id));
    }
  };

  const saveStaffOrder = async (staffOrder) => {
    try {
      const token = localStorage.getItem("token");
      await axios.post(`${API}/my-business/staff/reorder`, { staff_order: staffOrder }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Ordine aggiornato!");
      fetchData();
    } catch (error) {
      toast.error("Errore nell'aggiornamento dell'ordine");
    }
  };

  const openEditStaff = (member) => {
    setEditingStaff(member);
    setStaffForm({
      name: member.name || "",
      email: member.email || "",
      phone: member.phone || "",
      image_url: member.image_url || "",
      role: member.role || "employee",
      service_ids: getStaffServiceIds(member),
      is_active: member.is_active !== false,
      color: member.color || "",
      // Staff login
      has_login: member.has_login || false,
      password: "", // Don't show existing password
      // Staff permissions
      can_edit_insert: member.can_edit_insert !== false,
      can_delete: member.can_delete || false,
      can_view_all_staff: member.can_view_all_staff || false
    });
    setStaffDialogOpen(true);
  };

  // ==================== CALENDAR CRUD ====================
  const handleSaveCalendar = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem("token");
      const headers = { Authorization: `Bearer ${token}` };
      if (editingCalendar) {
        await axios.put(`${API}/my-business/calendars/${editingCalendar.calendar_id}`, calendarForm, { headers });
        toast.success("Calendario aggiornato!");
      } else {
        await axios.post(`${API}/my-business/calendars`, calendarForm, { headers });
        toast.success("Calendario creato!");
      }
      setCalendarDialogOpen(false);
      setEditingCalendar(null);
      setCalendarForm({
        name: "", description: "", service_ids: [], staff_id: "",
        slot_duration_minutes: 30, buffer_before: 0, buffer_after: 0, max_days_advance: 30,
        default_availability: []
      });
      fetchData();
    } catch (error) {
      toast.error("Errore durante il salvataggio");
    }
  };

  const handleDeleteCalendar = async (calendarId) => {
    const calendar = calendars.find(c => c.calendar_id === calendarId);
    setDeleteTarget({ type: "calendar", id: calendarId, name: calendar?.name || "Calendario" });
    setDeleteConfirmOpen(true);
  };

  const confirmDelete = async () => {
    if (!deleteTarget.id) return;
    setDeleting(true);
    
    try {
      const token = localStorage.getItem("token");
      let endpoint = "";
      
      switch (deleteTarget.type) {
        case "calendar":
          endpoint = `${API}/my-business/calendars/${deleteTarget.id}`;
          break;
        case "service":
          endpoint = `${API}/my-business/services/${deleteTarget.id}`;
          break;
        case "staff":
          endpoint = `${API}/my-business/staff/${deleteTarget.id}`;
          break;
        case "category":
          endpoint = `${API}/my-business/categories/${deleteTarget.id}`;
          break;
        default:
          throw new Error("Tipo eliminazione non valido");
      }
      
      await axios.delete(endpoint, { headers: { Authorization: `Bearer ${token}` } });
      toast.success(`${deleteTarget.name} eliminato con successo!`);
      setDeleteConfirmOpen(false);
      setDeleteTarget({ type: "", id: "", name: "" });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Errore durante l'eliminazione");
    } finally {
      setDeleting(false);
    }
  };

  const openEditCalendar = (calendar) => {
    setEditingCalendar(calendar);
    setCalendarForm({
      name: calendar.name || "",
      description: calendar.description || "",
      service_ids: calendar.service_ids || [],
      staff_id: calendar.staff_id || "",
      slot_duration_minutes: calendar.slot_duration_minutes || 30,
      buffer_before: calendar.buffer_before || 0,
      buffer_after: calendar.buffer_after || 0,
      max_days_advance: calendar.max_days_advance || 30,
      default_availability: calendar.default_availability || []
    });
    setCalendarDialogOpen(true);
  };

  const formatDateTime = (dateStr) => {
    if (!dateStr) return "Data non disponibile";
    // Remove trailing Z to treat as local time (not UTC)
    const cleanDateStr = dateStr.replace(/Z$/, '');
    const date = new Date(cleanDateStr);
    if (isNaN(date.getTime())) return "Data non valida";
    return date.toLocaleDateString("it-IT", { 
      weekday: "short",
      day: "2-digit", 
      month: "short",
      year: "numeric",
      hour: "2-digit", 
      minute: "2-digit" 
    });
  };

  // Format duration nicely
  const formatDuration = (minutes) => {
    if (!minutes || minutes <= 0) return null;
    if (minutes >= 1440) {
      const days = Math.floor(minutes / 1440);
      return `${days} giorn${days === 1 ? 'o' : 'i'}`;
    }
    if (minutes >= 60) {
      const hours = Math.floor(minutes / 60);
      const mins = minutes % 60;
      if (mins === 0) {
        return `${hours} or${hours === 1 ? 'a' : 'e'}`;
      }
      return `${hours}h ${mins}m`;
    }
    return `${minutes} min`;
  };

  // Calculate booking duration
  const getBookingDuration = (booking) => {
    if (booking.start_datetime && booking.end_datetime) {
      // Remove trailing Z to treat as local time
      const cleanStart = booking.start_datetime.replace(/Z$/, '');
      const cleanEnd = booking.end_datetime.replace(/Z$/, '');
      const start = new Date(cleanStart);
      const end = new Date(cleanEnd);
      const diffMs = end - start;
      const diffMins = Math.round(diffMs / 60000);
      return formatDuration(diffMins);
    }
    // Fallback: sum service durations
    if (booking.services_details?.length > 0) {
      const totalMins = booking.services_details.reduce((sum, s) => sum + (s.duration_minutes || 0), 0);
      return formatDuration(totalMins);
    }
    if (booking.services?.length > 0) {
      const totalMins = booking.services.reduce((sum, s) => sum + (s.duration_minutes || 0), 0);
      return formatDuration(totalMins);
    }
    return null;
  };

  // Booking detail functions
  const openBookingDetail = (booking) => {
    setSelectedBooking(booking);
    const startDate = booking.start_datetime ? parseISO(booking.start_datetime) : new Date();
    setBookingEditForm({
      customer_name: booking.customer_name || "",
      customer_email: booking.customer_email || "",
      customer_phone: booking.customer_phone || "",
      status: booking.status || "confirmed",
      notes: booking.notes || "",
      start_datetime: format(startDate, "yyyy-MM-dd"),
      start_time: format(startDate, "HH:mm")
    });
    setEditingBooking(false);
    setBookingDetailOpen(true);
  };

  const handleUpdateBooking = async () => {
    if (!selectedBooking) return;
    try {
      const token = localStorage.getItem("token");
      const [year, month, day] = bookingEditForm.start_datetime.split('-').map(Number);
      const [hours, minutes] = bookingEditForm.start_time.split(':').map(Number);
      const startDateTime = new Date(year, month - 1, day, hours, minutes);
      
      await axios.put(`${API}/my-business/bookings/${selectedBooking.booking_id}`, {
        customer_name: bookingEditForm.customer_name,
        customer_email: bookingEditForm.customer_email,
        customer_phone: bookingEditForm.customer_phone,
        status: bookingEditForm.status,
        notes: bookingEditForm.notes,
        start_datetime: startDateTime.toISOString()
      }, { headers: { Authorization: `Bearer ${token}` } });
      
      toast.success("Prenotazione aggiornata!");
      setBookingDetailOpen(false);
      fetchData();
    } catch (error) {
      toast.error("Errore nell'aggiornamento");
    }
  };

  const handleDeleteBooking = async () => {
    if (!selectedBooking || !confirm("Eliminare questa prenotazione?")) return;
    try {
      const token = localStorage.getItem("token");
      await axios.delete(`${API}/my-business/bookings/${selectedBooking.booking_id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Prenotazione eliminata!");
      setBookingDetailOpen(false);
      fetchData();
    } catch (error) {
      toast.error("Errore nell'eliminazione");
    }
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-96">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
        </div>
      </DashboardLayout>
    );
  }

  if (!business) {
    return (
      <DashboardLayout>
        <div className="flex flex-col items-center justify-center h-96">
          <Building2 className="h-16 w-16 text-slate-300 mb-4" />
          <h2 className="text-xl font-semibold text-slate-600">{t('business.noBusinessMessage')}</h2>
          <p className="text-slate-500 mt-2">{t('business.contactAdmin')}</p>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="p-3 sm:p-6 space-y-4 sm:space-y-6" data-testid="business-owner-dashboard">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div className="flex items-center gap-3 sm:gap-4">
            {business.logo_url ? (
              <img src={business.logo_url} alt={business.name} className="h-10 w-10 sm:h-14 sm:w-14 rounded-lg object-cover flex-shrink-0" />
            ) : (
              <div className="h-10 w-10 sm:h-14 sm:w-14 rounded-lg bg-gradient-to-br from-indigo-500 to-purple-500 flex items-center justify-center text-white text-lg sm:text-xl font-bold flex-shrink-0">
                {business.name?.charAt(0).toUpperCase()}
              </div>
            )}
            <div className="min-w-0">
              <h1 className="text-lg sm:text-2xl font-bold text-slate-900 truncate">{business.name}</h1>
              <div className="flex flex-wrap items-center gap-2 mt-1">
                <Badge variant="outline" className="text-xs">{business.subscription_plan?.name || "Piano Base"}</Badge>
                <a 
                  href={`/book/${business.slug}`} 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="text-xs sm:text-sm text-primary hover:underline flex items-center gap-1"
                >
                  <ExternalLink className="h-3 w-3" />
                  <span className="hidden sm:inline">Pagina prenotazioni</span>
                  <span className="sm:hidden">Prenota</span>
                </a>
              </div>
            </div>
          </div>
        </div>

        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="flex flex-wrap gap-1 h-auto p-1 max-w-6xl">
            <TabsTrigger value="agenda" data-testid="tab-agenda">
              <CalendarDays className="h-4 w-4 mr-1" />Agenda
            </TabsTrigger>
            <TabsTrigger value="overview" data-testid="tab-overview">
              <TrendingUp className="h-4 w-4 mr-1" />Overview
            </TabsTrigger>
            <TabsTrigger value="services" data-testid="tab-services">
              <Scissors className="h-4 w-4 mr-1" />Servizi
            </TabsTrigger>
            <TabsTrigger value="categories" data-testid="tab-categories">
              <Grid3X3 className="h-4 w-4 mr-1" />Categorie
            </TabsTrigger>
            <TabsTrigger value="bookings" data-testid="tab-bookings">
              <ClipboardList className="h-4 w-4 mr-1" />Presenze
            </TabsTrigger>
            <TabsTrigger value="staff" data-testid="tab-staff">
              <UserCircle className="h-4 w-4 mr-1" />Staff
            </TabsTrigger>
            <TabsTrigger value="calendars" data-testid="tab-calendars">
              <Calendar className="h-4 w-4 mr-1" />Orari
            </TabsTrigger>
            <TabsTrigger value="customers" data-testid="tab-customers">
              <Users className="h-4 w-4 mr-1" />Clienti
            </TabsTrigger>
            <TabsTrigger value="analytics" data-testid="tab-analytics">
              <BarChart3 className="h-4 w-4 mr-1" />Analytics
            </TabsTrigger>
            <TabsTrigger value="discounts" data-testid="tab-discounts">
              <Tag className="h-4 w-4 mr-1" />Sconti
            </TabsTrigger>
            <TabsTrigger value="api-keys" data-testid="tab-api-keys">
              <Key className="h-4 w-4 mr-1" />API
            </TabsTrigger>
            <TabsTrigger value="settings" data-testid="tab-settings">
              <Settings className="h-4 w-4 mr-1" />Impostazioni
            </TabsTrigger>
          </TabsList>

          {/* Agenda Tab - Calendar View */}
          <TabsContent value="agenda" className="mt-4">
            <CalendarView 
              businessId={business?.business_id} 
              staff={staff} 
              bookings={bookings} 
              calendars={calendars}
              categories={categories}
              services={services}
              onBookingCreated={fetchData}
              newBookingHighlight={newBookingHighlight}
            />
          </TabsContent>

          {/* Overview Tab */}
          <TabsContent value="overview" className="mt-4 sm:mt-6">
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 mb-4 sm:mb-6">
              <BusinessStatCard 
                title={t('business.stats.services')} 
                value={services.length} 
                icon={Scissors} 
                color="#4F46E5"
                onClick={() => setActiveTab("services")}
              />
              <BusinessStatCard 
                title={t('business.stats.staff')} 
                value={staff.length} 
                icon={Users} 
                color="#10B981"
                onClick={() => setActiveTab("staff")}
              />
              <BusinessStatCard 
                title={t('business.stats.bookings')} 
                value={business.stats?.total_bookings || 0} 
                icon={Calendar} 
                color="#F59E0B"
                onClick={() => setActiveTab("bookings")}
              />
              <BusinessStatCard 
                title={t('business.stats.revenue')} 
                value={`€${(business.stats?.total_revenue || 0).toFixed(2)}`} 
                icon={Euro} 
                color="#EC4899"
              />
            </div>

            {/* Plan Info */}
            {business.subscription_plan && (
              <Card className="mb-6">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Package className="h-5 w-5" />
                    Il Tuo Piano: {business.subscription_plan.name}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <div className="text-center p-3 bg-slate-50 rounded-lg">
                      <p className="text-2xl font-bold">{business.subscription_plan.max_calendars === -1 ? "∞" : business.subscription_plan.max_calendars}</p>
                      <p className="text-sm text-slate-500">Calendari</p>
                      <p className="text-xs text-slate-400">Usati: {calendars.length}</p>
                    </div>
                    <div className="text-center p-3 bg-slate-50 rounded-lg">
                      <p className="text-2xl font-bold">{business.subscription_plan.max_staff === -1 ? "∞" : business.subscription_plan.max_staff}</p>
                      <p className="text-sm text-slate-500">Staff</p>
                      <p className="text-xs text-slate-400">Usati: {staff.length}</p>
                    </div>
                    <div className="text-center p-3 bg-slate-50 rounded-lg">
                      <p className="text-2xl font-bold">{business.subscription_plan.stripe_enabled ? "✓" : "✗"}</p>
                      <p className="text-sm text-slate-500">Pagamenti</p>
                    </div>
                    <div className="text-center p-3 bg-slate-50 rounded-lg">
                      <p className="text-2xl font-bold">{business.subscription_plan.google_calendar_enabled ? "✓" : "✗"}</p>
                      <p className="text-sm text-slate-500">Google Calendar</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Recent Bookings */}
            <Card>
              <CardHeader>
                <CardTitle>Prossime Prenotazioni</CardTitle>
              </CardHeader>
              <CardContent>
                {bookings.length === 0 ? (
                  <p className="text-slate-500 text-center py-4">Nessuna prenotazione</p>
                ) : (
                  <div className="space-y-3">
                    {bookings.slice(0, 5).map((booking) => (
                      <div key={booking.booking_id} className="flex items-center justify-between p-3 border rounded-lg">
                        <div>
                          <p className="font-medium">{booking.customer_name}</p>
                          <p className="text-sm text-slate-500">{formatDateTime(booking.start_datetime)}</p>
                        </div>
                        <Badge variant={booking.status === "confirmed" ? "default" : booking.status === "pending" ? "secondary" : "outline"}>
                          {booking.status}
                        </Badge>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Services Tab */}
          <TabsContent value="services" className="mt-6">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between">
                <div>
                  <CardTitle>{t('services.title')}</CardTitle>
                  <CardDescription>{t('services.subtitle')}</CardDescription>
                </div>
                <Dialog open={serviceDialogOpen} onOpenChange={setServiceDialogOpen}>
                  <DialogTrigger asChild>
                    <Button data-testid="add-service-btn" onClick={() => { setEditingService(null); setServiceForm({ name: "", description: "", image_url: "", category_id: "", duration_minutes: 30, price: 0, currency: "EUR" }); }}>
                      <Plus className="h-4 w-4 mr-2" />
                      {t('services.add')}
                    </Button>
                  </DialogTrigger>
                  <DialogContent className="max-w-lg">
                    <DialogHeader>
                      <DialogTitle>{editingService ? t('services.edit') : t('services.add')}</DialogTitle>
                    </DialogHeader>
                    <form onSubmit={handleSaveService} className="space-y-4">
                      <div className="space-y-2">
                        <Label>{t('services.form.name')}</Label>
                        <Input value={serviceForm.name} onChange={(e) => setServiceForm({ ...serviceForm, name: e.target.value })} required />
                      </div>
                      <div className="space-y-2">
                        <Label>{t('services.form.category')}</Label>
                        <Select value={serviceForm.category_id || "none"} onValueChange={(v) => setServiceForm({ ...serviceForm, category_id: v === "none" ? "" : v })}>
                          <SelectTrigger><SelectValue placeholder={t('common.select')} /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="none">{t('categories.noCategory')}</SelectItem>
                            {categories.map((c) => (
                              <SelectItem key={c.category_id} value={c.category_id}>
                                <div className="flex items-center gap-2">
                                  <div className="w-3 h-3 rounded-full" style={{ backgroundColor: c.color || '#6B7280' }}></div>
                                  {c.name}
                                </div>
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="space-y-2">
                        <Label>{t('services.form.image')}</Label>
                        <ImageUpload value={serviceForm.image_url} onChange={(url) => setServiceForm({ ...serviceForm, image_url: url })} shape="square" />
                      </div>
                      <div className="grid grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label>{t('services.form.duration')}</Label>
                          <Input type="number" min="5" value={serviceForm.duration_minutes} onChange={(e) => setServiceForm({ ...serviceForm, duration_minutes: parseInt(e.target.value) || 30 })} />
                        </div>
                        <div className="space-y-2">
                          <Label>{t('services.form.price')}</Label>
                          <Input type="number" min="0" step="0.01" value={serviceForm.price} onChange={(e) => setServiceForm({ ...serviceForm, price: parseFloat(e.target.value) || 0 })} />
                        </div>
                      </div>
                      <div className="space-y-2">
                        <Label>{t('services.form.description')}</Label>
                        <Textarea value={serviceForm.description} onChange={(e) => setServiceForm({ ...serviceForm, description: e.target.value })} rows={2} />
                      </div>
                      {/* Multi-session settings */}
                      <div className="border-t pt-4 mt-2">
                        <Label className="text-sm font-medium mb-3 block">Tipo appuntamento</Label>
                        <div className="flex gap-4 mb-3">
                          <label className={`flex items-center gap-2 px-3 py-2 rounded-lg border cursor-pointer transition-colors ${serviceForm.session_type === 'single' ? 'bg-blue-50 border-blue-300' : 'bg-slate-50'}`}>
                            <input type="radio" name="session_type" value="single" checked={serviceForm.session_type === 'single'} onChange={() => setServiceForm({...serviceForm, session_type: 'single', num_sessions: 1})} className="h-4 w-4" />
                            <span className="text-sm font-medium">Seduta singola</span>
                          </label>
                          <label className={`flex items-center gap-2 px-3 py-2 rounded-lg border cursor-pointer transition-colors ${serviceForm.session_type === 'multi' ? 'bg-blue-50 border-blue-300' : 'bg-slate-50'}`}>
                            <input type="radio" name="session_type" value="multi" checked={serviceForm.session_type === 'multi'} onChange={() => setServiceForm({...serviceForm, session_type: 'multi', num_sessions: 2})} className="h-4 w-4" />
                            <span className="text-sm font-medium">Multi-seduta</span>
                          </label>
                        </div>
                        {serviceForm.session_type === 'multi' && (
                          <div className="grid grid-cols-2 gap-4 p-3 bg-blue-50/50 rounded-lg border border-blue-100">
                            <div className="space-y-1">
                              <Label className="text-xs">Numero sedute</Label>
                              <Input type="number" min="2" max="20" value={serviceForm.num_sessions} onChange={(e) => setServiceForm({...serviceForm, num_sessions: parseInt(e.target.value) || 2})} data-testid="num-sessions-input" />
                            </div>
                            <div className="space-y-1">
                              <Label className="text-xs">Intervallo (giorni)</Label>
                              <Input type="number" min="1" max="90" value={serviceForm.session_interval_days} onChange={(e) => setServiceForm({...serviceForm, session_interval_days: parseInt(e.target.value) || 7})} data-testid="session-interval-input" />
                            </div>
                          </div>
                        )}
                      </div>
                      <DialogFooter>
                        <Button type="submit">{editingService ? t('common.save') : t('common.create')}</Button>
                      </DialogFooter>
                    </form>
                  </DialogContent>
                </Dialog>
              </CardHeader>
              <CardContent>
                {/* Category Filter Buttons */}
                {categories.length > 0 && (
                  <div className="mb-4 flex gap-2 flex-wrap items-center">
                    <span className="text-sm text-slate-500 mr-2">Filtra per categoria:</span>
                    <button
                      onClick={() => setServiceCategoryFilter("all")}
                      className={`px-3 py-1 rounded-full text-xs font-medium transition-all ${
                        serviceCategoryFilter === "all" 
                          ? "bg-slate-800 text-white" 
                          : "bg-slate-100 text-slate-600 hover:bg-slate-200"
                      }`}
                      data-testid="filter-all-services"
                    >
                      Tutti ({services.length})
                    </button>
                    {categories.map((cat) => {
                      const count = services.filter(s => s.category_id === cat.category_id).length;
                      return (
                        <button
                          key={cat.category_id}
                          onClick={() => setServiceCategoryFilter(serviceCategoryFilter === cat.category_id ? "all" : cat.category_id)}
                          style={{
                            backgroundColor: serviceCategoryFilter === cat.category_id ? cat.color : `${cat.color}20`,
                            color: serviceCategoryFilter === cat.category_id ? '#fff' : cat.color
                          }}
                          className="px-3 py-1 rounded-full text-xs font-medium transition-all hover:opacity-80"
                          data-testid={`filter-category-${cat.category_id}`}
                        >
                          {cat.name} ({count})
                        </button>
                      );
                    })}
                    <button
                      onClick={() => setServiceCategoryFilter("none")}
                      className={`px-3 py-1 rounded-full text-xs font-medium transition-all ${
                        serviceCategoryFilter === "none" 
                          ? "bg-slate-500 text-white" 
                          : "bg-slate-100 text-slate-500 hover:bg-slate-200"
                      }`}
                      data-testid="filter-no-category"
                    >
                      Senza categoria ({services.filter(s => !s.category_id).length})
                    </button>
                  </div>
                )}
                
                {/* Services Grid - Drag & Drop */}
                {(() => {
                  const filteredServices = services
                    .filter(service => {
                      if (serviceCategoryFilter === "all") return true;
                      if (serviceCategoryFilter === "none") return !service.category_id;
                      return service.category_id === serviceCategoryFilter;
                    })
                    .sort((a, b) => (a.order || 0) - (b.order || 0));
                  const serviceIds = filteredServices.map(s => s.service_id);

                  return filteredServices.length > 0 ? (
                    <DndContext sensors={dndSensors} collisionDetection={closestCenter} onDragEnd={handleServiceDragEnd}>
                      <SortableContext items={serviceIds} strategy={rectSortingStrategy}>
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="services-grid">
                          {filteredServices.map((service) => {
                            const cat = categories.find(c => c.category_id === service.category_id);
                            return (
                              <SortableServiceCard key={service.service_id} id={service.service_id}>
                                {({ listeners, isDragging }) => (
                                  <Card className={`overflow-hidden transition-shadow ${isDragging ? 'shadow-xl ring-2 ring-blue-400' : 'hover:shadow-md'} ${service.is_active === false ? 'opacity-60' : ''}`} data-testid={`service-card-${service.service_id}`}>
                                    {/* Drag handle */}
                                    <div
                                      {...listeners}
                                      className="flex items-center justify-center gap-1 py-1.5 bg-slate-50 border-b cursor-grab active:cursor-grabbing hover:bg-slate-100 transition-colors"
                                      title="Tieni premuto e trascina per riordinare"
                                      data-testid={`service-drag-handle-${service.service_id}`}
                                    >
                                      <GripVertical className="h-4 w-4 text-slate-400" />
                                      <span className="text-[10px] text-slate-400 uppercase tracking-wider font-medium select-none">Trascina</span>
                                    </div>
                                    {/* Service Image */}
                                    {service.image_url ? (
                                      <div className="relative w-full h-20 bg-slate-100">
                                        <img src={service.image_url} alt={service.name} className="w-full h-full object-cover" />
                                        {cat && (
                                          <div className="absolute top-2 left-2 px-2 py-0.5 rounded-full text-xs font-medium text-white" style={{ backgroundColor: cat.color || '#6B7280' }}>
                                            {cat.name}
                                          </div>
                                        )}
                                        {service.is_active === false && <Badge variant="destructive" className="absolute top-2 right-2 text-xs">Disattivo</Badge>}
                                      </div>
                                    ) : (
                                      <div className="h-24 bg-gradient-to-br from-slate-100 to-slate-200 flex items-center justify-center relative">
                                        <ImageIcon className="h-8 w-8 text-slate-300" />
                                        {cat && (
                                          <div className="absolute top-2 left-2 px-2 py-0.5 rounded-full text-xs font-medium text-white" style={{ backgroundColor: cat.color || '#6B7280' }}>
                                            {cat.name}
                                          </div>
                                        )}
                                        {service.is_active === false && <Badge variant="destructive" className="absolute top-2 right-2 text-xs">Disattivo</Badge>}
                                      </div>
                                    )}
                                    <CardContent className="p-4">
                                      <div className="flex items-start justify-between">
                                        <div className="flex-1 min-w-0">
                                          <div className="flex items-center gap-2">
                                            <div className="w-2 h-2 rounded-full flex-shrink-0" style={{ backgroundColor: cat?.color || "#6B7280" }} />
                                            <h4 className="font-semibold text-slate-900 truncate">{service.name}</h4>
                                          </div>
                                          {service.description && <p className="text-xs text-slate-500 mt-1 line-clamp-2">{service.description}</p>}
                                          <div className="flex items-center gap-3 mt-3 text-sm">
                                            <span className="flex items-center gap-1 text-slate-500">
                                              <Clock className="h-3 w-3" />{service.duration_minutes} min
                                            </span>
                                            <span className="font-bold text-slate-900">
                                              {service.price > 0 ? `€${service.price.toFixed(2)}` : "Gratuito"}
                                            </span>
                                            {service.session_type === 'multi' && (
                                              <Badge variant="outline" className="text-[10px] px-1.5 py-0 border-blue-300 text-blue-600">{service.num_sessions} sedute</Badge>
                                            )}
                                          </div>
                                        </div>
                                      </div>
                                      {/* Action buttons */}
                                      <div className="flex items-center justify-between mt-4 pt-3 border-t">
                                        <Button 
                                          variant="ghost" size="icon" className="h-7 w-7" 
                                          onClick={() => handleToggleServiceActive(service)}
                                          title={service.is_active === false ? "Attiva servizio" : "Disattiva servizio"}
                                        >
                                          {service.is_active === false ? <ToggleLeft className="h-4 w-4 text-slate-400" /> : <ToggleRight className="h-4 w-4 text-green-500" />}
                                        </Button>
                                        <div className="flex gap-1">
                                          <Button variant="outline" size="sm" className="h-7" onClick={() => openEditService(service)} data-testid={`edit-service-${service.service_id}`}>
                                            <Pencil className="h-3 w-3 mr-1" />Modifica
                                          </Button>
                                          <Button variant="ghost" size="icon" className="h-7 w-7 text-red-500 hover:text-red-700" onClick={() => handleDeleteService(service.service_id)}>
                                            <Trash2 className="h-3 w-3" />
                                          </Button>
                                        </div>
                                      </div>
                                    </CardContent>
                                  </Card>
                                )}
                              </SortableServiceCard>
                            );
                          })}
                        </div>
                      </SortableContext>
                    </DndContext>
                  ) : (
                    <div className="text-center py-8 text-slate-500">
                      <Package className="h-12 w-12 mx-auto mb-3 text-slate-300" />
                      <p>Nessun servizio in questa categoria</p>
                    </div>
                  );
                })()}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Categories Tab */}
          <TabsContent value="categories" className="mt-6">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between">
                <div>
                  <CardTitle>{t('categories.title', 'Categorie')}</CardTitle>
                  <CardDescription>{t('categories.subtitle', 'Organizza i tuoi servizi in categorie')}</CardDescription>
                </div>
                <Dialog open={categoryDialogOpen} onOpenChange={setCategoryDialogOpen}>
                  <DialogTrigger asChild>
                    <Button data-testid="add-category-btn" onClick={() => { setEditingCategory(null); setCategoryForm({ name: "", color: "#4F46E5", order: 0 }); }}>
                      <Plus className="h-4 w-4 mr-2" />
                      {t('categories.add', 'Nuova Categoria')}
                    </Button>
                  </DialogTrigger>
                  <DialogContent>
                    <DialogHeader>
                      <DialogTitle>{editingCategory ? t('categories.edit', 'Modifica Categoria') : t('categories.add', 'Nuova Categoria')}</DialogTitle>
                    </DialogHeader>
                    <form onSubmit={handleSaveCategory} className="space-y-4">
                      <div>
                        <Label>{t('common.name', 'Nome')}</Label>
                        <Input
                          value={categoryForm.name}
                          onChange={(e) => setCategoryForm({ ...categoryForm, name: e.target.value })}
                          required
                          data-testid="category-name-input"
                        />
                      </div>
                      <div>
                        <Label>{t('common.color', 'Colore')}</Label>
                        <div className="flex items-center gap-2">
                          <Input
                            type="color"
                            value={categoryForm.color}
                            onChange={(e) => setCategoryForm({ ...categoryForm, color: e.target.value })}
                            className="w-16 h-10 p-1"
                          />
                          <Input
                            value={categoryForm.color}
                            onChange={(e) => setCategoryForm({ ...categoryForm, color: e.target.value })}
                            className="flex-1"
                          />
                        </div>
                      </div>
                      <DialogFooter>
                        <Button type="submit" data-testid="save-category-btn">
                          <Save className="h-4 w-4 mr-2" />
                          {t('common.save', 'Salva')}
                        </Button>
                      </DialogFooter>
                    </form>
                  </DialogContent>
                </Dialog>
              </CardHeader>
              <CardContent>
                {categories.length === 0 ? (
                  <div className="text-center py-8 text-slate-500">
                    <Grid3X3 className="h-12 w-12 mx-auto mb-3 text-slate-300" />
                    <p>{t('categories.empty', 'Nessuna categoria. Crea la prima!')}</p>
                  </div>
                ) : (
                  <DndContext sensors={dndSensors} collisionDetection={closestCenter} onDragEnd={handleCategoryDragEnd}>
                    <SortableContext items={[...categories].sort((a, b) => (a.order || 0) - (b.order || 0)).map(c => c.category_id)} strategy={rectSortingStrategy}>
                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="categories-grid">
                        {[...categories].sort((a, b) => (a.order || 0) - (b.order || 0)).map((category) => {
                          const categoryServicesCount = services.filter(s => s.category_id === category.category_id).length;
                          return (
                            <SortableCategoryCard key={category.category_id} id={category.category_id}>
                              {({ listeners, isDragging }) => (
                                <div className={`p-4 border rounded-lg relative group transition-shadow ${isDragging ? 'shadow-xl ring-2 ring-blue-400 bg-white' : 'hover:bg-slate-50'}`} data-testid={`category-card-${category.category_id}`}>
                                  {/* Color bar at top */}
                                  <div className="absolute top-0 left-0 right-0 h-1 rounded-t-lg" style={{ backgroundColor: category.color || "#6B7280" }} />
                                  
                                  <div className="flex items-start justify-between mt-1">
                                    <div className="flex items-center gap-3 flex-1 min-w-0">
                                      {/* Drag handle */}
                                      <div
                                        {...listeners}
                                        className="cursor-grab active:cursor-grabbing p-1 rounded hover:bg-slate-200 transition-colors"
                                        title="Tieni premuto e trascina per riordinare"
                                        data-testid={`category-drag-handle-${category.category_id}`}
                                      >
                                        <GripVertical className="h-4 w-4 text-slate-400" />
                                      </div>
                                      <div className="w-4 h-4 rounded-full flex-shrink-0" style={{ backgroundColor: category.color || "#6B7280" }} />
                                      <div className="min-w-0">
                                        <h4 className="font-medium truncate">{category.name}</h4>
                                        <p className="text-sm text-slate-500">{categoryServicesCount} {t('services.title', 'servizi')}</p>
                                      </div>
                                    </div>
                                    <div className="flex gap-1 flex-shrink-0">
                                      <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => openEditCategory(category)}>
                                        <Pencil className="h-4 w-4" />
                                      </Button>
                                      <Button variant="ghost" size="icon" className="h-8 w-8 text-red-500" onClick={() => handleDeleteCategory(category.category_id)}>
                                        <Trash2 className="h-4 w-4" />
                                      </Button>
                                    </div>
                                  </div>
                                </div>
                              )}
                            </SortableCategoryCard>
                          );
                        })}
                      </div>
                    </SortableContext>
                  </DndContext>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Staff Tab */}
          <TabsContent value="staff" className="mt-6">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between">
                <div>
                  <CardTitle>{t('staff.title')}</CardTitle>
                  <CardDescription>{t('staff.subtitle')}</CardDescription>
                </div>
                <Dialog open={staffDialogOpen} onOpenChange={setStaffDialogOpen}>
                  <DialogTrigger asChild>
                    <Button data-testid="add-staff-btn" onClick={() => { setEditingStaff(null); setStaffForm({ name: "", email: "", phone: "", image_url: "", role: "employee", service_ids: [], is_active: true, color: "" }); }}>
                      <Plus className="h-4 w-4 mr-2" />
                      {t('staff.add')}
                    </Button>
                  </DialogTrigger>
                  <DialogContent className="max-h-[90vh] overflow-y-auto">
                    <DialogHeader>
                      <DialogTitle>{editingStaff ? t('staff.edit') : t('staff.add')}</DialogTitle>
                    </DialogHeader>
                    <form onSubmit={handleSaveStaff} className="space-y-4">
                      <div className="space-y-2">
                        <Label>{t('staff.form.image')}</Label>
                        <ImageUpload value={staffForm.image_url} onChange={(url) => setStaffForm({ ...staffForm, image_url: url })} shape="round" />
                      </div>
                      <div className="space-y-2">
                        <Label>{t('staff.form.name')}</Label>
                        <Input value={staffForm.name} onChange={(e) => setStaffForm({ ...staffForm, name: e.target.value })} required />
                      </div>
                      <div className="grid grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label>{t('staff.form.email')}</Label>
                          <Input type="email" value={staffForm.email} onChange={(e) => setStaffForm({ ...staffForm, email: e.target.value })} />
                        </div>
                        <div className="space-y-2">
                          <Label>{t('staff.form.phone')}</Label>
                          <Input value={staffForm.phone} onChange={(e) => setStaffForm({ ...staffForm, phone: e.target.value })} />
                        </div>
                      </div>
                      <div className="space-y-2">
                        <Label>{t('staff.form.role')}</Label>
                        <Select value={staffForm.role} onValueChange={(v) => setStaffForm({ ...staffForm, role: v })}>
                          <SelectTrigger><SelectValue /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="manager">{t('staff.roles.manager')}</SelectItem>
                            <SelectItem value="employee">{t('staff.roles.employee')}</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="space-y-2">
                        <Label>Colore Agenda</Label>
                        <div className="flex items-center gap-3">
                          <div className="flex gap-1.5 flex-wrap">
                            {["#3B82F6","#EF4444","#10B981","#F59E0B","#8B5CF6","#EC4899","#06B6D4","#F97316","#6366F1","#14B8A6"].map(c => (
                              <button
                                key={c}
                                type="button"
                                className={`w-7 h-7 rounded-full border-2 transition-all ${staffForm.color === c ? 'border-slate-900 scale-110 ring-2 ring-offset-1 ring-slate-400' : 'border-transparent hover:scale-105'}`}
                                style={{ backgroundColor: c }}
                                onClick={() => setStaffForm({ ...staffForm, color: c })}
                                data-testid={`staff-color-${c}`}
                              />
                            ))}
                          </div>
                          <input
                            type="color"
                            value={staffForm.color || "#3B82F6"}
                            onChange={(e) => setStaffForm({ ...staffForm, color: e.target.value })}
                            className="w-8 h-8 rounded cursor-pointer border-0 p-0"
                            title="Colore personalizzato"
                          />
                        </div>
                        {staffForm.color && (
                          <button type="button" className="text-xs text-slate-500 hover:text-red-500" onClick={() => setStaffForm({ ...staffForm, color: "" })}>
                            Rimuovi colore
                          </button>
                        )}
                      </div>
                      {services.length > 0 && (
                        <div className="space-y-2">
                          <div className="flex items-center justify-between">
                            <Label>{t('staff.form.services')}</Label>
                            <div className="flex gap-2">
                              <Button 
                                type="button" 
                                variant="outline" 
                                size="sm"
                                onClick={() => setStaffForm({ ...staffForm, service_ids: services.map(s => s.service_id) })}
                              >
                                Seleziona tutti
                              </Button>
                              <Button 
                                type="button" 
                                variant="outline" 
                                size="sm"
                                onClick={() => setStaffForm({ ...staffForm, service_ids: [] })}
                              >
                                Deseleziona tutti
                              </Button>
                            </div>
                          </div>
                          <div className="space-y-4 max-h-60 overflow-y-auto border rounded-md p-3">
                            {/* Group services by category */}
                            {categories.map((cat) => {
                              const catServices = services.filter(s => s.category_id === cat.category_id);
                              if (catServices.length === 0) return null;
                              const allCatSelected = catServices.every(s => staffForm.service_ids.includes(s.service_id));
                              const someCatSelected = catServices.some(s => staffForm.service_ids.includes(s.service_id));
                              return (
                                <div key={cat.category_id}>
                                  <div className="flex items-center gap-2 mb-2 pb-1 border-b">
                                    <Checkbox 
                                      id={`staff-cat-${cat.category_id}`}
                                      checked={allCatSelected}
                                      className={someCatSelected && !allCatSelected ? "opacity-50" : ""}
                                      onCheckedChange={(checked) => {
                                        if (checked) {
                                          const newIds = [...new Set([...staffForm.service_ids, ...catServices.map(s => s.service_id)])];
                                          setStaffForm({ ...staffForm, service_ids: newIds });
                                        } else {
                                          const catServiceIds = catServices.map(s => s.service_id);
                                          setStaffForm({ ...staffForm, service_ids: staffForm.service_ids.filter(id => !catServiceIds.includes(id)) });
                                        }
                                      }}
                                    />
                                    <div className="w-3 h-3 rounded-full" style={{ backgroundColor: cat.color || '#6B7280' }}></div>
                                    <label htmlFor={`staff-cat-${cat.category_id}`} className="text-sm font-semibold">{cat.name}</label>
                                    <span className="text-xs text-slate-500">({catServices.filter(s => staffForm.service_ids.includes(s.service_id)).length}/{catServices.length})</span>
                                  </div>
                                  <div className="grid grid-cols-2 gap-1 pl-5">
                                    {catServices.map((s) => (
                                      <div key={s.service_id} className="flex items-center gap-2">
                                        <Checkbox 
                                          id={`staff-srv-${s.service_id}`} 
                                          checked={staffForm.service_ids.includes(s.service_id)} 
                                          onCheckedChange={(checked) => setStaffForm({ 
                                            ...staffForm, 
                                            service_ids: checked 
                                              ? [...staffForm.service_ids, s.service_id] 
                                              : staffForm.service_ids.filter(id => id !== s.service_id) 
                                          })} 
                                        />
                                        <label htmlFor={`staff-srv-${s.service_id}`} className="text-sm truncate">{s.name}</label>
                                      </div>
                                    ))}
                                  </div>
                                </div>
                              );
                            })}
                            {/* Services without category */}
                            {services.filter(s => !s.category_id).length > 0 && (
                              <div>
                                <div className="flex items-center gap-2 mb-2 pb-1 border-b">
                                  <Checkbox 
                                    id="staff-cat-none"
                                    checked={services.filter(s => !s.category_id).every(s => staffForm.service_ids.includes(s.service_id))}
                                    onCheckedChange={(checked) => {
                                      const uncatServices = services.filter(s => !s.category_id);
                                      if (checked) {
                                        const newIds = [...new Set([...staffForm.service_ids, ...uncatServices.map(s => s.service_id)])];
                                        setStaffForm({ ...staffForm, service_ids: newIds });
                                      } else {
                                        const uncatIds = uncatServices.map(s => s.service_id);
                                        setStaffForm({ ...staffForm, service_ids: staffForm.service_ids.filter(id => !uncatIds.includes(id)) });
                                      }
                                    }}
                                  />
                                  <div className="w-3 h-3 rounded-full bg-slate-400"></div>
                                  <label htmlFor="staff-cat-none" className="text-sm font-semibold text-slate-600">Senza categoria</label>
                                </div>
                                <div className="grid grid-cols-2 gap-1 pl-5">
                                  {services.filter(s => !s.category_id).map((s) => (
                                    <div key={s.service_id} className="flex items-center gap-2">
                                      <Checkbox 
                                        id={`staff-srv-${s.service_id}`} 
                                        checked={staffForm.service_ids.includes(s.service_id)} 
                                        onCheckedChange={(checked) => setStaffForm({ 
                                          ...staffForm, 
                                          service_ids: checked 
                                            ? [...staffForm.service_ids, s.service_id] 
                                            : staffForm.service_ids.filter(id => id !== s.service_id) 
                                        })} 
                                      />
                                      <label htmlFor={`staff-srv-${s.service_id}`} className="text-sm truncate">{s.name}</label>
                                    </div>
                                  ))}
                                </div>
                              </div>
                            )}
                          </div>
                        </div>
                      )}
                      {/* Staff Active Switch - only show when editing */}
                      {editingStaff && (
                        <div className="flex items-center justify-between p-3 border rounded-lg bg-slate-50">
                          <div>
                            <Label className="font-medium">Stato Staff</Label>
                            <p className="text-xs text-slate-500">Disattiva per nascondere questo staff dalle prenotazioni</p>
                          </div>
                          <Switch 
                            checked={staffForm.is_active} 
                            onCheckedChange={(checked) => setStaffForm({ ...staffForm, is_active: checked })} 
                            data-testid="staff-active-switch"
                          />
                        </div>
                      )}
                      
                      {/* Staff Web Access Section */}
                      <div className="border rounded-lg p-4 space-y-4 bg-slate-50">
                        <div className="flex items-center justify-between">
                          <div>
                            <Label className="font-medium">Accesso Web Portale Staff</Label>
                            <p className="text-xs text-slate-500">Abilita per permettere allo staff di accedere all'agenda online</p>
                          </div>
                          <Switch 
                            checked={staffForm.has_login} 
                            onCheckedChange={(checked) => setStaffForm({ ...staffForm, has_login: checked })} 
                          />
                        </div>
                        
                        {staffForm.has_login && (
                          <div className="space-y-4 pt-3 border-t">
                            <div className="space-y-2">
                              <Label>Password {!editingStaff && "*"}</Label>
                              <Input 
                                type="password"
                                value={staffForm.password}
                                onChange={(e) => setStaffForm({ ...staffForm, password: e.target.value })}
                                placeholder={editingStaff ? "Lascia vuoto per mantenere la password attuale" : "Imposta password"}
                                required={!editingStaff && staffForm.has_login}
                              />
                              <p className="text-xs text-slate-500">L'email sopra sarà usata come username</p>
                            </div>
                            
                            <div className="space-y-3">
                              <Label className="font-medium">Permessi</Label>
                              
                              <div className="flex items-center justify-between p-2 border rounded bg-white">
                                <div>
                                  <span className="text-sm font-medium">Modifica e Inserimento</span>
                                  <p className="text-xs text-slate-500">Può creare e modificare appuntamenti</p>
                                </div>
                                <Switch 
                                  checked={staffForm.can_edit_insert} 
                                  onCheckedChange={(checked) => setStaffForm({ ...staffForm, can_edit_insert: checked })} 
                                />
                              </div>
                              
                              <div className="flex items-center justify-between p-2 border rounded bg-white">
                                <div>
                                  <span className="text-sm font-medium">Eliminazione</span>
                                  <p className="text-xs text-slate-500">Può eliminare appuntamenti</p>
                                </div>
                                <Switch 
                                  checked={staffForm.can_delete} 
                                  onCheckedChange={(checked) => setStaffForm({ ...staffForm, can_delete: checked })} 
                                />
                              </div>
                              
                              <div className="flex items-center justify-between p-2 border rounded bg-white">
                                <div>
                                  <span className="text-sm font-medium">Visualizza tutto il salone</span>
                                  <p className="text-xs text-slate-500">Può vedere gli appuntamenti di tutti gli staff</p>
                                </div>
                                <Switch 
                                  checked={staffForm.can_view_all_staff} 
                                  onCheckedChange={(checked) => setStaffForm({ ...staffForm, can_view_all_staff: checked })} 
                                />
                              </div>
                            </div>
                            
                            {editingStaff?.has_login && (
                              <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg">
                                <p className="text-sm text-blue-700">
                                  <strong>URL Accesso Staff:</strong><br/>
                                  <code className="text-xs">{window.location.origin}/staff-login</code>
                                </p>
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                      
                      <DialogFooter>
                        <Button type="submit">{editingStaff ? t('common.save') : t('common.add')}</Button>
                      </DialogFooter>
                    </form>
                  </DialogContent>
                </Dialog>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {[...staff].sort((a, b) => (a.order || 0) - (b.order || 0)).map((member, index, sortedArr) => (
                    <div key={member.staff_id} className={`p-4 border rounded-lg text-center relative group ${member.is_active === false ? 'opacity-60 bg-slate-50' : ''}`}
                      style={{ borderTopWidth: '3px', borderTopColor: member.color || '#e2e8f0' }}
                    >
                      <div className="absolute top-2 left-2 opacity-0 group-hover:opacity-100 transition-opacity flex flex-col gap-1">
                        <Button 
                          variant="ghost" 
                          size="icon" 
                          className="h-6 w-6"
                          onClick={() => moveStaff(member.staff_id, "up")}
                          disabled={index === 0}
                          title="Sposta su"
                        >
                          <ChevronUp className="h-3 w-3" />
                        </Button>
                        <Button 
                          variant="ghost" 
                          size="icon" 
                          className="h-6 w-6"
                          onClick={() => moveStaff(member.staff_id, "down")}
                          disabled={index === sortedArr.length - 1}
                          title="Sposta giù"
                        >
                          <ChevronDown className="h-3 w-3" />
                        </Button>
                      </div>
                      <div className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity flex gap-1">
                        <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => openEditStaff(member)}>
                          <Pencil className="h-3 w-3" />
                        </Button>
                        <Button variant="ghost" size="icon" className="h-7 w-7 text-red-500" onClick={() => handleDeleteStaff(member.staff_id)}>
                          <Trash2 className="h-3 w-3" />
                        </Button>
                      </div>
                      {member.image_url ? (
                        <img src={member.image_url} alt={member.name} className="w-16 h-16 rounded-full mx-auto object-cover" style={{ border: member.color ? `3px solid ${member.color}` : undefined }} />
                      ) : (
                        <div className="w-16 h-16 rounded-full mx-auto flex items-center justify-center text-white text-xl font-bold"
                          style={{ backgroundColor: member.color || '#6366f1' }}
                        >
                          {member.name?.charAt(0).toUpperCase()}
                        </div>
                      )}
                      <h4 className="font-medium mt-3">{member.name}</h4>
                      {member.is_active === false && (
                        <Badge variant="secondary" className="text-xs mt-1">Disattivato</Badge>
                      )}
                      <p className="text-sm text-slate-500">{member.email}</p>
                      {member.service_ids?.length > 0 && (
                        <p className="text-xs text-slate-400 mt-1">{member.service_ids.length} {t('dashboard.services').toLowerCase()}</p>
                      )}
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Calendars/Orari Tab */}
          <TabsContent value="calendars" className="mt-6">
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle>Calendari e Orari</CardTitle>
                    <CardDescription>Gestisci gli orari di apertura e i calendari dello staff</CardDescription>
                  </div>
                  <Dialog open={calendarDialogOpen} onOpenChange={setCalendarDialogOpen}>
                    <DialogTrigger asChild>
                      <Button onClick={() => {
                        setEditingCalendar(null);
                        setCalendarForm({
                          name: "", description: "", service_ids: [], staff_id: "",
                          slot_duration_minutes: 30, buffer_before: 0, buffer_after: 0, max_days_advance: 30,
                          default_availability: [
                            { day_of_week: 0, periods: [{ start_time: "09:00", end_time: "13:00" }, { start_time: "14:00", end_time: "18:00" }] },
                            { day_of_week: 1, periods: [{ start_time: "09:00", end_time: "13:00" }, { start_time: "14:00", end_time: "18:00" }] },
                            { day_of_week: 2, periods: [{ start_time: "09:00", end_time: "13:00" }, { start_time: "14:00", end_time: "18:00" }] },
                            { day_of_week: 3, periods: [{ start_time: "09:00", end_time: "13:00" }, { start_time: "14:00", end_time: "18:00" }] },
                            { day_of_week: 4, periods: [{ start_time: "09:00", end_time: "13:00" }, { start_time: "14:00", end_time: "18:00" }] },
                          ]
                        });
                      }} data-testid="add-calendar-btn">
                        <Plus className="h-4 w-4 mr-2" />Aggiungi Calendario
                      </Button>
                    </DialogTrigger>
                    <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
                      <DialogHeader>
                        <DialogTitle>{editingCalendar ? "Modifica Calendario" : "Nuovo Calendario"}</DialogTitle>
                      </DialogHeader>
                      <form onSubmit={handleSaveCalendar} className="space-y-4">
                        <div className="grid grid-cols-2 gap-4">
                          <div className="space-y-2">
                            <Label>Nome Calendario *</Label>
                            <Input value={calendarForm.name} onChange={(e) => setCalendarForm({ ...calendarForm, name: e.target.value })} required />
                          </div>
                          <div className="space-y-2">
                            <Label>Assegna a Staff</Label>
                            <Select value={calendarForm.staff_id || "none"} onValueChange={(v) => setCalendarForm({ ...calendarForm, staff_id: v === "none" ? "" : v })}>
                              <SelectTrigger><SelectValue placeholder="Tutti (Manager)" /></SelectTrigger>
                              <SelectContent>
                                <SelectItem value="none">Tutti (Manager)</SelectItem>
                                {staff.map((s) => <SelectItem key={s.staff_id} value={s.staff_id}>{s.name}</SelectItem>)}
                              </SelectContent>
                            </Select>
                          </div>
                        </div>
                        {services.length > 0 && (
                          <div className="space-y-2">
                            <div className="flex items-center justify-between">
                              <Label>Servizi disponibili</Label>
                              <div className="flex gap-2">
                                <Button 
                                  type="button" 
                                  variant="outline" 
                                  size="sm"
                                  onClick={() => setCalendarForm({ ...calendarForm, service_ids: services.map(s => s.service_id) })}
                                >
                                  Seleziona tutti
                                </Button>
                                <Button 
                                  type="button" 
                                  variant="outline" 
                                  size="sm"
                                  onClick={() => setCalendarForm({ ...calendarForm, service_ids: [] })}
                                >
                                  Deseleziona tutti
                                </Button>
                              </div>
                            </div>
                            <div className="border rounded-md p-3 max-h-60 overflow-y-auto space-y-4">
                              {/* Group services by category */}
                              {categories.map((cat) => {
                                const catServices = services.filter(s => s.category_id === cat.category_id);
                                if (catServices.length === 0) return null;
                                const allCatSelected = catServices.every(s => calendarForm.service_ids.includes(s.service_id));
                                const someCatSelected = catServices.some(s => calendarForm.service_ids.includes(s.service_id));
                                return (
                                  <div key={cat.category_id}>
                                    <div className="flex items-center gap-2 mb-2 pb-1 border-b">
                                      <Checkbox 
                                        id={`cal-cat-${cat.category_id}`}
                                        checked={allCatSelected}
                                        className={someCatSelected && !allCatSelected ? "opacity-50" : ""}
                                        onCheckedChange={(checked) => {
                                          if (checked) {
                                            // Select all services in category
                                            const newIds = [...new Set([...calendarForm.service_ids, ...catServices.map(s => s.service_id)])];
                                            setCalendarForm({ ...calendarForm, service_ids: newIds });
                                          } else {
                                            // Deselect all services in category
                                            const catServiceIds = catServices.map(s => s.service_id);
                                            setCalendarForm({ ...calendarForm, service_ids: calendarForm.service_ids.filter(id => !catServiceIds.includes(id)) });
                                          }
                                        }}
                                      />
                                      <div className="w-3 h-3 rounded-full" style={{ backgroundColor: cat.color || '#6B7280' }}></div>
                                      <label htmlFor={`cal-cat-${cat.category_id}`} className="text-sm font-semibold">{cat.name}</label>
                                      <span className="text-xs text-slate-500">({catServices.filter(s => calendarForm.service_ids.includes(s.service_id)).length}/{catServices.length})</span>
                                    </div>
                                    <div className="grid grid-cols-2 gap-1 pl-5">
                                      {catServices.map((s) => (
                                        <div key={s.service_id} className="flex items-center gap-2">
                                          <Checkbox 
                                            id={`cal-srv-${s.service_id}`} 
                                            checked={calendarForm.service_ids.includes(s.service_id)} 
                                            onCheckedChange={(checked) => setCalendarForm({ 
                                              ...calendarForm, 
                                              service_ids: checked 
                                                ? [...calendarForm.service_ids, s.service_id] 
                                                : calendarForm.service_ids.filter(id => id !== s.service_id) 
                                            })} 
                                          />
                                          <label htmlFor={`cal-srv-${s.service_id}`} className="text-sm truncate">{s.name}</label>
                                        </div>
                                      ))}
                                    </div>
                                  </div>
                                );
                              })}
                              {/* Services without category */}
                              {services.filter(s => !s.category_id).length > 0 && (
                                <div>
                                  <div className="flex items-center gap-2 mb-2 pb-1 border-b">
                                    <Checkbox 
                                      id="cal-cat-none"
                                      checked={services.filter(s => !s.category_id).every(s => calendarForm.service_ids.includes(s.service_id))}
                                      onCheckedChange={(checked) => {
                                        const uncatServices = services.filter(s => !s.category_id);
                                        if (checked) {
                                          const newIds = [...new Set([...calendarForm.service_ids, ...uncatServices.map(s => s.service_id)])];
                                          setCalendarForm({ ...calendarForm, service_ids: newIds });
                                        } else {
                                          const uncatIds = uncatServices.map(s => s.service_id);
                                          setCalendarForm({ ...calendarForm, service_ids: calendarForm.service_ids.filter(id => !uncatIds.includes(id)) });
                                        }
                                      }}
                                    />
                                    <div className="w-3 h-3 rounded-full bg-slate-400"></div>
                                    <label htmlFor="cal-cat-none" className="text-sm font-semibold text-slate-600">Senza categoria</label>
                                  </div>
                                  <div className="grid grid-cols-2 gap-1 pl-5">
                                    {services.filter(s => !s.category_id).map((s) => (
                                      <div key={s.service_id} className="flex items-center gap-2">
                                        <Checkbox 
                                          id={`cal-srv-${s.service_id}`} 
                                          checked={calendarForm.service_ids.includes(s.service_id)} 
                                          onCheckedChange={(checked) => setCalendarForm({ 
                                            ...calendarForm, 
                                            service_ids: checked 
                                              ? [...calendarForm.service_ids, s.service_id] 
                                              : calendarForm.service_ids.filter(id => id !== s.service_id) 
                                          })} 
                                        />
                                        <label htmlFor={`cal-srv-${s.service_id}`} className="text-sm truncate">{s.name}</label>
                                      </div>
                                    ))}
                                  </div>
                                </div>
                              )}
                            </div>
                          </div>
                        )}
                        <div className="grid grid-cols-4 gap-4">
                          <div className="space-y-2">
                            <Label>Durata Slot (min)</Label>
                            <Input type="number" min="5" value={calendarForm.slot_duration_minutes} onChange={(e) => setCalendarForm({ ...calendarForm, slot_duration_minutes: parseInt(e.target.value) || 30 })} />
                          </div>
                          <div className="space-y-2">
                            <Label>Buffer Prima</Label>
                            <Input type="number" min="0" value={calendarForm.buffer_before} onChange={(e) => setCalendarForm({ ...calendarForm, buffer_before: parseInt(e.target.value) || 0 })} />
                          </div>
                          <div className="space-y-2">
                            <Label>Buffer Dopo</Label>
                            <Input type="number" min="0" value={calendarForm.buffer_after} onChange={(e) => setCalendarForm({ ...calendarForm, buffer_after: parseInt(e.target.value) || 0 })} />
                          </div>
                          <div className="space-y-2">
                            <Label>Max Giorni</Label>
                            <Input type="number" min="1" value={calendarForm.max_days_advance} onChange={(e) => setCalendarForm({ ...calendarForm, max_days_advance: parseInt(e.target.value) || 30 })} />
                          </div>
                        </div>
                        <div className="space-y-2">
                          <Label>Orari di Apertura</Label>
                          <div className="space-y-2 border rounded-md p-3">
                            {[0, 1, 2, 3, 4, 5, 6].map((dayNum) => {
                              const dayAvail = calendarForm.default_availability.find(d => d.day_of_week === dayNum);
                              const isActive = !!dayAvail;
                              // Python convention: 0=Mon, 1=Tue, 2=Wed, 3=Thu, 4=Fri, 5=Sat, 6=Sun
                              const dayNames = { 0: "Lun", 1: "Mar", 2: "Mer", 3: "Gio", 4: "Ven", 5: "Sab", 6: "Dom" };
                              return (
                                <div key={dayNum} className="flex items-center gap-3">
                                  <div className="w-16">
                                    <Checkbox id={`day-${dayNum}`} checked={isActive} onCheckedChange={(checked) => {
                                      if (checked) setCalendarForm({ ...calendarForm, default_availability: [...calendarForm.default_availability, { day_of_week: dayNum, periods: [{ start_time: "09:00", end_time: "18:00" }] }] });
                                      else setCalendarForm({ ...calendarForm, default_availability: calendarForm.default_availability.filter(d => d.day_of_week !== dayNum) });
                                    }} />
                                    <label htmlFor={`day-${dayNum}`} className="ml-2 text-sm font-medium">{dayNames[dayNum]}</label>
                                  </div>
                                  {isActive && (
                                    <div className="flex-1 flex flex-wrap gap-2">
                                      {dayAvail.periods.map((period, idx) => (
                                        <div key={idx} className="flex items-center gap-1 bg-slate-50 rounded px-2 py-1">
                                          <Input type="time" value={period.start_time} onChange={(e) => {
                                            const newAvail = [...calendarForm.default_availability];
                                            const dayIdx = newAvail.findIndex(d => d.day_of_week === dayNum);
                                            newAvail[dayIdx].periods[idx].start_time = e.target.value;
                                            setCalendarForm({ ...calendarForm, default_availability: newAvail });
                                          }} className="w-24 h-8 text-sm" />
                                          <span>-</span>
                                          <Input type="time" value={period.end_time} onChange={(e) => {
                                            const newAvail = [...calendarForm.default_availability];
                                            const dayIdx = newAvail.findIndex(d => d.day_of_week === dayNum);
                                            newAvail[dayIdx].periods[idx].end_time = e.target.value;
                                            setCalendarForm({ ...calendarForm, default_availability: newAvail });
                                          }} className="w-24 h-8 text-sm" />
                                          {dayAvail.periods.length > 1 && (
                                            <Button type="button" variant="ghost" size="icon" className="h-6 w-6 text-red-500" onClick={() => {
                                              const newAvail = [...calendarForm.default_availability];
                                              const dayIdx = newAvail.findIndex(d => d.day_of_week === dayNum);
                                              newAvail[dayIdx].periods = newAvail[dayIdx].periods.filter((_, i) => i !== idx);
                                              setCalendarForm({ ...calendarForm, default_availability: newAvail });
                                            }}><X className="h-4 w-4" /></Button>
                                          )}
                                        </div>
                                      ))}
                                      <Button type="button" variant="outline" size="sm" onClick={() => {
                                        const newAvail = [...calendarForm.default_availability];
                                        const dayIdx = newAvail.findIndex(d => d.day_of_week === dayNum);
                                        newAvail[dayIdx].periods.push({ start_time: "14:00", end_time: "18:00" });
                                        setCalendarForm({ ...calendarForm, default_availability: newAvail });
                                      }}><Plus className="h-3 w-3 mr-1" />Fascia</Button>
                                    </div>
                                  )}
                                </div>
                              );
                            })}
                          </div>
                        </div>
                        <DialogFooter>
                          <Button type="submit" data-testid="save-calendar-btn">{editingCalendar ? "Salva" : "Crea"}</Button>
                        </DialogFooter>
                      </form>
                    </DialogContent>
                  </Dialog>
                </div>
              </CardHeader>
              <CardContent>
                {calendars.length === 0 ? (
                  <div className="text-center py-8 text-slate-500">
                    <Calendar className="h-12 w-12 mx-auto mb-3 text-slate-300" />
                    <p>Nessun calendario configurato.</p>
                    <p className="text-sm">Aggiungi un calendario per gestire gli orari di apertura.</p>
                  </div>
                ) : (
                  <div className="grid md:grid-cols-2 gap-4">
                    {calendars.map((cal) => {
                      const assignedStaff = staff.find(s => s.staff_id === cal.staff_id);
                      const calendarLink = `${window.location.origin}/book/${business?.slug}?calendar=${cal.calendar_id}`;
                      return (
                        <Card key={cal.calendar_id} className="border-slate-200">
                          <CardContent className="p-4">
                            <div className="flex items-start justify-between mb-3">
                              <div>
                                <h4 className="font-semibold">{cal.name}</h4>
                                <Badge variant="outline" className="mt-1">{assignedStaff ? assignedStaff.name : "Tutti (Manager)"}</Badge>
                              </div>
                              <div className="flex gap-1">
                                <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => openEditCalendar(cal)}><Pencil className="h-4 w-4" /></Button>
                                <Button variant="ghost" size="icon" className="h-8 w-8 text-red-500" onClick={() => handleDeleteCalendar(cal.calendar_id)}><Trash2 className="h-4 w-4" /></Button>
                              </div>
                            </div>
                            <div className="text-sm space-y-1 text-slate-600">
                              <p>Slot: {cal.slot_duration_minutes} min</p>
                              <p>Anticipo max: {cal.max_days_advance} giorni</p>
                              {cal.service_ids?.length > 0 && <p>Servizi: {cal.service_ids.length}</p>}
                            </div>
                            {/* Calendar Link */}
                            <div className="mt-3 pt-3 border-t border-slate-100">
                              <div className="flex items-center gap-2">
                                <Input 
                                  value={calendarLink} 
                                  readOnly 
                                  className="text-xs h-8 bg-slate-50"
                                />
                                <Button 
                                  variant="outline" 
                                  size="sm" 
                                  className="h-8 px-2"
                                  onClick={() => copyCalendarLink(cal.calendar_id)}
                                >
                                  {copiedCalendarId === cal.calendar_id ? (
                                    <Check className="h-4 w-4 text-green-500" />
                                  ) : (
                                    <Copy className="h-4 w-4" />
                                  )}
                                </Button>
                                <Button 
                                  variant="outline" 
                                  size="sm" 
                                  className="h-8 px-2"
                                  onClick={() => window.open(calendarLink, '_blank')}
                                >
                                  <ExternalLink className="h-4 w-4" />
                                </Button>
                              </div>
                            </div>
                          </CardContent>
                        </Card>
                      );
                    })}
                  </div>
                )}
              </CardContent>
            </Card>
            <BlockedDatesManager apiBasePath="/my-business" />
            <StaffAbsencesManager businessId={business?.business_id} staff={staff} />
          </TabsContent>

          {/* Customers Tab */}
          <TabsContent value="customers" className="mt-6">
            <CustomersManager role="business" />
          </TabsContent>

          {/* Bookings Tab - Presenze */}
          <TabsContent value="bookings" className="mt-4">
            <BookingsListView 
              bookings={bookings}
              onRefresh={fetchData}
              isAdmin={false}
              business={business}
            />
          </TabsContent>

          {/* Analytics Tab */}
          <TabsContent value="analytics" className="mt-6">
            <AnalyticsView 
              businessId={business?.business_id} 
              staff={staff}
              services={services}
            />
          </TabsContent>

          {/* Discounts Tab */}
          <TabsContent value="discounts" className="mt-6">
            <DiscountCodesManager 
              apiBasePath="/my-business"
              services={services}
              categories={categories}
            />
          </TabsContent>

          {/* API Keys Tab */}
          <TabsContent value="api-keys" className="mt-6">
            <ApiKeysManager apiBasePath="/my-business" />
          </TabsContent>

          {/* Settings Tab */}
          <TabsContent value="settings" className="mt-6">
            {/* Sub-tabs for Settings */}
            <div className="flex flex-wrap gap-2 mb-6 border-b pb-4">
              <Button 
                variant={settingsSubTab === "general" ? "default" : "outline"} 
                size="sm"
                onClick={() => setSettingsSubTab("general")}
                data-testid="settings-subtab-general"
              >
                <Building2 className="h-4 w-4 mr-1" />Dati Attività
              </Button>
              <Button 
                variant={settingsSubTab === "integration" ? "default" : "outline"} 
                size="sm"
                onClick={() => setSettingsSubTab("integration")}
                data-testid="settings-subtab-integration"
              >
                <Grid3X3 className="h-4 w-4 mr-1" />Integrazioni
              </Button>
              <Button 
                variant={settingsSubTab === "payment" ? "default" : "outline"} 
                size="sm"
                onClick={() => setSettingsSubTab("payment")}
                data-testid="settings-subtab-payment"
              >
                <CreditCard className="h-4 w-4 mr-1" />Pagamenti
              </Button>
              <Button 
                variant={settingsSubTab === "subscription" ? "default" : "outline"} 
                size="sm"
                onClick={() => setSettingsSubTab("subscription")}
                data-testid="settings-subtab-subscription"
              >
                <Crown className="h-4 w-4 mr-1" />Abbonamento
              </Button>
              <Button 
                variant={settingsSubTab === "email" ? "default" : "outline"} 
                size="sm"
                onClick={() => setSettingsSubTab("email")}
                data-testid="settings-subtab-email"
              >
                <Mail className="h-4 w-4 mr-1" />Email
              </Button>
              <Button 
                variant={settingsSubTab === "whatsapp" ? "default" : "outline"} 
                size="sm"
                onClick={() => setSettingsSubTab("whatsapp")}
                data-testid="settings-subtab-whatsapp"
              >
                <MessageCircle className="h-4 w-4 mr-1" />WhatsApp
              </Button>
              <Button 
                variant={settingsSubTab === "push" ? "default" : "outline"} 
                size="sm"
                onClick={() => setSettingsSubTab("push")}
                data-testid="settings-subtab-push"
              >
                <Bell className="h-4 w-4 mr-1" />Notifiche Push
              </Button>
            </div>
            
            {/* GENERAL - Dati Attività */}
            {settingsSubTab === "general" && (
            <Card>
              <CardHeader className="flex flex-row items-center justify-between">
                <div>
                  <CardTitle>{t('business.settings.title')}</CardTitle>
                  <CardDescription>{t('business.settings.subtitle')}</CardDescription>
                </div>
                {!editingBusiness ? (
                  <Button onClick={() => setEditingBusiness(true)} data-testid="edit-business-btn">
                    <Edit className="h-4 w-4 mr-2" />
                    {t('common.edit')}
                  </Button>
                ) : (
                  <div className="flex gap-2">
                    <Button variant="outline" onClick={() => { setEditingBusiness(false); setBusinessForm(business); }}>
                      {t('common.cancel')}
                    </Button>
                    <Button onClick={handleUpdateBusiness} data-testid="save-business-btn">
                      <Save className="h-4 w-4 mr-2" />
                      {t('common.save')}
                    </Button>
                  </div>
                )}
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-4">
                    <div>
                      <Label>{t('business.settings.name')}</Label>
                      <Input 
                        value={businessForm.name || ""}
                        onChange={(e) => setBusinessForm({...businessForm, name: e.target.value})}
                        disabled={!editingBusiness}
                      />
                    </div>
                    <div>
                      <Label>{t('business.settings.email')}</Label>
                      <Input 
                        type="email"
                        value={businessForm.email || ""}
                        onChange={(e) => setBusinessForm({...businessForm, email: e.target.value})}
                        disabled={!editingBusiness}
                      />
                    </div>
                    <div>
                      <Label>{t('business.settings.phone')}</Label>
                      <Input 
                        value={businessForm.phone || ""}
                        onChange={(e) => setBusinessForm({...businessForm, phone: e.target.value})}
                        disabled={!editingBusiness}
                      />
                    </div>
                  </div>
                  <div className="space-y-4">
                    <div>
                      <Label>{t('business.settings.address')}</Label>
                      <Input 
                        value={businessForm.address || ""}
                        onChange={(e) => setBusinessForm({...businessForm, address: e.target.value})}
                        disabled={!editingBusiness}
                      />
                    </div>
                    <div>
                      <Label>{t('business.settings.description')}</Label>
                      <Textarea 
                        value={businessForm.description || ""}
                        onChange={(e) => setBusinessForm({...businessForm, description: e.target.value})}
                        disabled={!editingBusiness}
                        rows={3}
                      />
                    </div>
                    <div>
                      <Label className="flex items-center gap-2">
                        <ImageIcon className="h-4 w-4" />
                        Immagine Profilo / Logo
                      </Label>
                      <p className="text-xs text-slate-500 mt-1 mb-2">
                        Questa immagine verrà mostrata nella pagina di prenotazione pubblica
                      </p>
                      {editingBusiness ? (
                        <ImageUpload 
                          value={businessForm.logo_url} 
                          onChange={(url) => setBusinessForm({ ...businessForm, logo_url: url })} 
                        />
                      ) : (
                        <div className="flex items-center gap-3">
                          {businessForm.logo_url ? (
                            <img 
                              src={businessForm.logo_url} 
                              alt="Logo azienda" 
                              className="w-20 h-20 rounded-lg object-cover border"
                            />
                          ) : (
                            <div className="w-20 h-20 rounded-lg bg-slate-100 flex items-center justify-center border">
                              <Building2 className="h-8 w-8 text-slate-400" />
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                    <div>
                      <Label className="flex items-center gap-2">
                        <MapPin className="h-4 w-4" />
                        Coordinate Mappa (per email)
                      </Label>
                      <p className="text-xs text-slate-500 mt-1 mb-2">
                        Inserisci le coordinate per mostrare la mappa nelle email di conferma
                      </p>
                      <div className="flex gap-3 mt-2">
                        <div className="flex-1">
                          <Label className="text-xs text-slate-500">Latitudine</Label>
                          <Input 
                            type="number"
                            step="any"
                            value={businessForm.latitude || ""}
                            onChange={(e) => setBusinessForm({...businessForm, latitude: e.target.value})}
                            disabled={!editingBusiness}
                            placeholder="45.4642"
                            data-testid="latitude-input"
                          />
                        </div>
                        <div className="flex-1">
                          <Label className="text-xs text-slate-500">Longitudine</Label>
                          <Input 
                            type="number"
                            step="any"
                            value={businessForm.longitude || ""}
                            onChange={(e) => setBusinessForm({...businessForm, longitude: e.target.value})}
                            disabled={!editingBusiness}
                            placeholder="10.6313"
                            data-testid="longitude-input"
                          />
                        </div>
                      </div>
                      {businessForm.latitude && businessForm.longitude && (
                        <div className="mt-2">
                          <a 
                            href={`https://www.google.com/maps/search/?api=1&query=${businessForm.latitude},${businessForm.longitude}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-xs text-primary hover:underline flex items-center gap-1"
                          >
                            <MapPin className="h-3 w-3" /> Verifica su Google Maps
                          </a>
                        </div>
                      )}
                    </div>
                    <div>
                      <Label className="flex items-center gap-2">
                        <Palette className="h-4 w-4" />
                        {t('business.settings.theme')}
                      </Label>
                      <div className="flex items-center gap-3 mt-2">
                        <input 
                          type="color"
                          value={businessForm.background_color || "#4F46E5"}
                          onChange={(e) => setBusinessForm({...businessForm, background_color: e.target.value})}
                          disabled={!editingBusiness}
                          className="w-12 h-10 rounded border cursor-pointer disabled:opacity-50"
                        />
                        <Input 
                          value={businessForm.background_color || "#4F46E5"}
                          onChange={(e) => setBusinessForm({...businessForm, background_color: e.target.value})}
                          disabled={!editingBusiness}
                          className="w-32"
                          placeholder="#4F46E5"
                        />
                      </div>
                    </div>
                    <div>
                      <Label className="flex items-center gap-2">
                        <Globe className="h-4 w-4" />
                        {t('business.settings.language')}
                      </Label>
                      <Select
                        value={businessForm.default_language || "it"}
                        onValueChange={(value) => setBusinessForm({...businessForm, default_language: value})}
                        disabled={!editingBusiness}
                      >
                        <SelectTrigger className="w-full mt-2">
                          <SelectValue placeholder={t('common.select')} />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="it">🇮🇹 {t('languages.it')}</SelectItem>
                          <SelectItem value="en">🇬🇧 {t('languages.en')}</SelectItem>
                          <SelectItem value="de">🇩🇪 {t('languages.de')}</SelectItem>
                          <SelectItem value="es">🇪🇸 {t('languages.es')}</SelectItem>
                        </SelectContent>
                      </Select>
                      <p className="text-xs text-slate-500 mt-1">
                        {t('business.settings.languageHelp')}
                      </p>
                    </div>
                  </div>
                  
                  {/* URL Redirect dopo prenotazione */}
                  <div className="border-t pt-4 mt-4">
                    <Label className="text-sm font-medium">URL Redirect dopo prenotazione</Label>
                    <p className="text-xs text-slate-500 mb-2">
                      Il cliente verrà reindirizzato a questo URL dopo aver completato la prenotazione (lascia vuoto per pagina di conferma standard)
                    </p>
                    <Input
                      placeholder="https://tuosito.com/grazie"
                      value={businessForm.redirect_url_after_booking || ""}
                      onChange={(e) => setBusinessForm({...businessForm, redirect_url_after_booking: e.target.value})}
                    />
                  </div>
                  
                  {/* Appuntamenti Ricorrenti */}
                  <div className="border-t pt-4 mt-4">
                    <Label className="text-sm font-medium">Appuntamenti Ricorrenti</Label>
                    <div className="flex items-center gap-3 mt-2">
                      <Checkbox 
                        id="recurring-enabled" 
                        checked={businessForm.recurring_appointments_enabled || false}
                        onCheckedChange={(checked) => setBusinessForm({...businessForm, recurring_appointments_enabled: checked})}
                      />
                      <label htmlFor="recurring-enabled" className="text-sm">
                        Permetti ai clienti di prenotare appuntamenti ricorrenti (settimanali, bisettimanali, mensili)
                      </label>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
            )}

            {/* INTEGRATION - Integrazioni */}
            {settingsSubTab === "integration" && (
            <>
            {/* Embed/Iframe Integration */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Grid3X3 className="h-5 w-5" />
                  Integrazione sul tuo Sito Web
                </CardTitle>
                <CardDescription>
                  Incorpora il widget di prenotazione nel tuo sito web
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <Label className="text-sm font-medium">Link Diretto alla Pagina di Prenotazione</Label>
                  <div className="flex items-center gap-2 mt-2">
                    <Input 
                      readOnly 
                      value={`${window.location.origin}/book/${business.slug}`}
                      className="font-mono text-sm"
                    />
                    <Button 
                      variant="outline" 
                      size="sm"
                      onClick={() => {
                        navigator.clipboard.writeText(`${window.location.origin}/book/${business.slug}`);
                        toast.success("Link copiato!");
                      }}
                    >
                      Copia
                    </Button>
                  </div>
                </div>
                
                <div>
                  <Label className="text-sm font-medium">Codice Iframe da incorporare</Label>
                  <p className="text-xs text-slate-500 mt-1 mb-2">
                    Copia e incolla questo codice nel tuo sito web per mostrare il widget di prenotazione
                  </p>
                  <div className="bg-slate-900 rounded-lg p-4 overflow-x-auto">
                    <code className="text-green-400 text-xs sm:text-sm whitespace-pre-wrap break-all">
{`<iframe 
  src="${window.location.origin}/book/${business.slug}" 
  width="100%" 
  height="800" 
  frameborder="0"
  style="border: none; border-radius: 8px;"
  title="Prenota un appuntamento"
></iframe>`}
                    </code>
                  </div>
                  <div className="flex gap-2 mt-3">
                    <Button 
                      variant="outline" 
                      size="sm"
                      onClick={() => {
                        const iframeCode = `<iframe src="${window.location.origin}/book/${business.slug}" width="100%" height="800" frameborder="0" style="border: none; border-radius: 8px;" title="Prenota un appuntamento"></iframe>`;
                        navigator.clipboard.writeText(iframeCode);
                        toast.success("Codice iframe copiato!");
                      }}
                    >
                      Copia Codice
                    </Button>
                    <Button 
                      variant="outline" 
                      size="sm"
                      onClick={() => window.open(`/book/${business.slug}`, '_blank')}
                    >
                      <ExternalLink className="h-4 w-4 mr-1" />
                      Anteprima
                    </Button>
                  </div>
                </div>

                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <h4 className="text-sm font-medium text-blue-800 mb-2">{t('business.settings.integrationTips')}</h4>
                  <ul className="text-xs text-blue-700 space-y-1">
                    <li>• {t('business.settings.tipWidth')}</li>
                    <li>• {t('business.settings.tipHeight')}</li>
                    <li>• {t('business.settings.tipResponsive')}</li>
                    <li>• {t('business.settings.tipBrowsers')}</li>
                  </ul>
                </div>
              </CardContent>
            </Card>

            {/* Google Calendar Integration */}
            <Card className="mt-6">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Calendar className="h-5 w-5" />
                  Google Calendar
                </CardTitle>
                <CardDescription>
                  Sincronizza automaticamente le prenotazioni con il tuo Google Calendar
                </CardDescription>
              </CardHeader>
              <CardContent>
                {!gcalStatus.configured ? (
                  <div className="text-center py-4 bg-amber-50 rounded-lg border border-amber-200">
                    <p className="text-amber-700">
                      Google Calendar non è configurato per questa piattaforma.
                    </p>
                    <p className="text-sm text-amber-600 mt-1">
                      Contatta l&apos;amministratore per abilitare l&apos;integrazione.
                    </p>
                  </div>
                ) : gcalStatus.connected ? (
                  <div className="space-y-4">
                    <div className="flex items-center justify-between p-4 bg-green-50 rounded-lg border border-green-200">
                      <div className="flex items-center gap-3">
                        <div className="h-10 w-10 rounded-full bg-green-100 flex items-center justify-center">
                          <Calendar className="h-5 w-5 text-green-600" />
                        </div>
                        <div>
                          <p className="font-medium text-green-800">Collegato</p>
                          <p className="text-sm text-green-600">{gcalStatus.email}</p>
                        </div>
                      </div>
                      <Button 
                        variant="outline" 
                        className="text-red-600 hover:bg-red-50"
                        onClick={handleDisconnectGoogleCalendar}
                        data-testid="disconnect-gcal-btn"
                      >
                        <Unlink className="h-4 w-4 mr-2" />
                        Disconnetti
                      </Button>
                    </div>
                    
                    {/* Calendar Selection */}
                    <div className="p-4 bg-slate-50 rounded-lg border">
                      <Label className="text-sm font-medium mb-2 block">Seleziona Calendario</Label>
                      <p className="text-xs text-slate-500 mb-3">Scegli su quale calendario Google sincronizzare le prenotazioni</p>
                      {loadingGoogleCalendars ? (
                        <div className="flex items-center gap-2 text-slate-500">
                          <Loader2 className="h-4 w-4 animate-spin" /> Caricamento calendari...
                        </div>
                      ) : googleCalendars.length > 0 ? (
                        <>
                          <Select 
                            value={gcalStatus.selected_calendar_id || "primary"} 
                            onValueChange={(val) => {
                              const cal = googleCalendars.find(c => c.id === val);
                              handleSelectGoogleCalendar(val, cal?.summary || "Calendario principale");
                            }}
                          >
                            <SelectTrigger data-testid="gcal-select-trigger">
                              <SelectValue placeholder="Seleziona calendario" />
                            </SelectTrigger>
                            <SelectContent>
                              {googleCalendars.map(cal => (
                                <SelectItem key={cal.id} value={cal.id}>
                                  <div className="flex items-center gap-2">
                                    <div className="w-3 h-3 rounded-full" style={{ backgroundColor: cal.backgroundColor }}></div>
                                    {cal.summary} {cal.primary && "(Principale)"}
                                  </div>
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                          {gcalStatus.selected_calendar_name && (
                            <p className="text-xs text-slate-500 mt-2">
                              Calendario attivo: <span className="font-medium">{gcalStatus.selected_calendar_name}</span>
                            </p>
                          )}
                        </>
                      ) : (
                        <p className="text-sm text-slate-500">Nessun calendario disponibile</p>
                      )}
                    </div>
                  </div>
                ) : (
                  <div className="text-center py-6">
                    <div className="h-16 w-16 rounded-full bg-slate-100 flex items-center justify-center mx-auto mb-4">
                      <Calendar className="h-8 w-8 text-slate-400" />
                    </div>
                    <p className="text-slate-600 mb-4">
                      Collega il tuo account Google per sincronizzare automaticamente le prenotazioni
                    </p>
                    <Button 
                      onClick={handleConnectGoogleCalendar}
                      disabled={connectingGcal}
                      data-testid="connect-gcal-btn"
                    >
                      {connectingGcal ? (
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      ) : (
                        <Link2 className="h-4 w-4 mr-2" />
                      )}
                      Collega Google Calendar
                    </Button>
                  </div>
                )}
              </CardContent>
            </Card>
            </>
            )}

            {/* PAYMENT - Pagamenti */}
            {settingsSubTab === "payment" && (
            <>
            {/* Stripe Connect Integration */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <CreditCard className="h-5 w-5" />
                  Stripe Connect
                </CardTitle>
                <CardDescription>
                  Collega il tuo account Stripe per ricevere i pagamenti delle prenotazioni
                </CardDescription>
              </CardHeader>
              <CardContent>
                {!stripeConnectStatus.platform_configured ? (
                  <div className="text-center py-4 bg-amber-50 rounded-lg border border-amber-200">
                    <p className="text-amber-700">
                      Stripe Connect non è configurato per questa piattaforma.
                    </p>
                    <p className="text-sm text-amber-600 mt-1">
                      Contatta l&apos;amministratore per abilitare l&apos;integrazione.
                    </p>
                  </div>
                ) : stripeConnectStatus.connected ? (
                  <div className="flex items-center justify-between p-4 bg-green-50 rounded-lg border border-green-200">
                    <div className="flex items-center gap-3">
                      <div className="h-10 w-10 rounded-full bg-green-100 flex items-center justify-center">
                        <CreditCard className="h-5 w-5 text-green-600" />
                      </div>
                      <div>
                        <p className="font-medium text-green-800">Collegato</p>
                        <p className="text-sm text-green-600">Account ID: {stripeConnectStatus.account_id}</p>
                      </div>
                    </div>
                    <Button 
                      variant="outline" 
                      className="text-red-600 hover:bg-red-50"
                      onClick={handleDisconnectStripe}
                      data-testid="disconnect-stripe-btn"
                    >
                      <Unlink className="h-4 w-4 mr-2" />
                      Disconnetti
                    </Button>
                  </div>
                ) : (
                  <div className="text-center py-6">
                    <div className="h-16 w-16 rounded-full bg-slate-100 flex items-center justify-center mx-auto mb-4">
                      <CreditCard className="h-8 w-8 text-slate-400" />
                    </div>
                    <p className="text-slate-600 mb-4">
                      Collega il tuo account Stripe per ricevere i pagamenti direttamente sul tuo conto
                    </p>
                    <Button 
                      onClick={handleConnectStripe}
                      disabled={connectingStripe}
                      data-testid="connect-stripe-btn"
                    >
                      {connectingStripe ? (
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      ) : (
                        <Link2 className="h-4 w-4 mr-2" />
                      )}
                      Collega Stripe
                    </Button>
                  </div>
                )}
              </CardContent>
            </Card>
            </>
            )}

            {/* SUBSCRIPTION - Abbonamento */}
            {settingsSubTab === "subscription" && (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Crown className="h-5 w-5" />
                  Piano di Abbonamento
                </CardTitle>
                <CardDescription>
                  Gestisci il tuo piano di abbonamento per sbloccare più funzionalità
                </CardDescription>
              </CardHeader>
              <CardContent>
                {subscription?.has_subscription && subscription?.plan ? (
                  <div className="space-y-4">
                    <div className="flex items-center justify-between p-4 bg-gradient-to-r from-indigo-50 to-purple-50 rounded-lg border border-indigo-200">
                      <div className="flex items-center gap-3">
                        <div className="h-12 w-12 rounded-full bg-indigo-100 flex items-center justify-center">
                          <Crown className="h-6 w-6 text-indigo-600" />
                        </div>
                        <div>
                          <p className="font-semibold text-indigo-900">{subscription.plan.name}</p>
                          <p className="text-sm text-indigo-600">
                            €{subscription.plan.price_monthly?.toFixed(2)}/mese
                          </p>
                          {subscription.status === "canceling" && (
                            <Badge className="mt-1 bg-amber-100 text-amber-700">
                              Cancellato - attivo fino a fine periodo
                            </Badge>
                          )}
                        </div>
                      </div>
                      <div className="text-right">
                        {subscription.current_period_end && (
                          <p className="text-xs text-slate-500">
                            Rinnovo: {new Date(subscription.current_period_end).toLocaleDateString("it-IT")}
                          </p>
                        )}
                        {subscription.status !== "canceling" && (
                          <Button 
                            variant="outline" 
                            size="sm"
                            className="mt-2 text-red-600 hover:bg-red-50"
                            onClick={handleCancelSubscription}
                            data-testid="cancel-subscription-btn"
                          >
                            Cancella Abbonamento
                          </Button>
                        )}
                      </div>
                    </div>
                    <div className="text-sm text-slate-500">
                      <p className="font-medium mb-2">Funzionalità incluse:</p>
                      <ul className="space-y-1">
                        {subscription.plan.features?.map((feature, i) => (
                          <li key={i} className="flex items-center gap-2">
                            <CheckCircle className="h-4 w-4 text-green-500" />
                            {feature}
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                ) : (
                  <div>
                    <p className="text-slate-600 mb-4 text-center">
                      Scegli un piano per sbloccare più funzionalità
                    </p>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      {subscriptionPlans.map((plan) => (
                        <div 
                          key={plan.plan_id}
                          className={`p-4 rounded-lg border-2 transition-all ${
                            plan.price_monthly === 0 
                              ? "border-slate-200 bg-slate-50" 
                              : "border-indigo-200 bg-indigo-50 hover:border-indigo-400"
                          }`}
                        >
                          <h4 className="font-semibold text-lg">{plan.name}</h4>
                          <p className="text-2xl font-bold mt-2">
                            {plan.price_monthly === 0 ? "Gratis" : `€${plan.price_monthly?.toFixed(2)}`}
                            {plan.price_monthly > 0 && <span className="text-sm font-normal text-slate-500">/mese</span>}
                          </p>
                          <ul className="mt-3 space-y-1 text-sm">
                            {plan.features?.slice(0, 3).map((feature, i) => (
                              <li key={i} className="flex items-center gap-2">
                                <CheckCircle className="h-3 w-3 text-green-500" />
                                {feature}
                              </li>
                            ))}
                          </ul>
                          {plan.price_monthly > 0 && (
                            <Button 
                              className="w-full mt-4"
                              onClick={() => handleSubscribe(plan.plan_id)}
                              disabled={subscribing}
                              data-testid={`subscribe-${plan.plan_id}`}
                            >
                              {subscribing ? (
                                <Loader2 className="h-4 w-4 animate-spin" />
                              ) : (
                                "Abbonati"
                              )}
                            </Button>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
            )}

            {/* EMAIL - Email Templates */}
            {settingsSubTab === "email" && (
            <div className="space-y-6">
              {/* Additional Message for Confirmation Emails */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 text-base">
                    <Mail className="h-5 w-5" />
                    Messaggio Aggiuntivo Email
                  </CardTitle>
                  <CardDescription>
                    Inserisci un messaggio personalizzato che verrà incluso nelle email di conferma prenotazione
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="space-y-2">
                    <Label>Messaggio aggiuntivo</Label>
                    <textarea
                      className="w-full min-h-[100px] rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                      placeholder="Es: Ricorda di portare un documento d'identità. / Si prega di arrivare 10 minuti prima dell'appuntamento."
                      defaultValue={business?.email_additional_message || ""}
                      data-testid="email-additional-message"
                      onChange={(e) => setBusiness(prev => ({ ...prev, email_additional_message: e.target.value }))}
                    />
                    <p className="text-xs text-slate-500">Questo messaggio apparirà nelle email di conferma inviate ai clienti</p>
                  </div>
                  <Button 
                    onClick={async () => {
                      try {
                        const token = localStorage.getItem("token");
                        await axios.put(`${API}/my-business`, { email_additional_message: business?.email_additional_message || "" }, { headers: { Authorization: `Bearer ${token}` } });
                        toast.success("Messaggio salvato");
                      } catch (error) {
                        toast.error("Errore nel salvataggio");
                      }
                    }}
                    data-testid="save-email-message-btn"
                  >
                    Salva Messaggio
                  </Button>
                </CardContent>
              </Card>
              
              <EmailTemplatesManager role="business" />
            </div>
            )}

            {/* WHATSAPP - Configurazione WhatsApp */}
            {settingsSubTab === "whatsapp" && (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-base">
                  <MessageCircle className="h-5 w-5 text-green-600" />
                  Configurazione WhatsApp
                </CardTitle>
                <CardDescription>
                  Imposta il numero e il messaggio pre-compilato per contattare i clienti via WhatsApp
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="space-y-2">
                  <Label>Numero WhatsApp</Label>
                  <Input
                    value={businessForm.whatsapp_number || ""}
                    onChange={(e) => setBusinessForm({...businessForm, whatsapp_number: e.target.value})}
                    placeholder="+39 333 1234567"
                    data-testid="whatsapp-number-input"
                  />
                  <p className="text-xs text-slate-500">Con prefisso internazionale (es. +39 per l'Italia)</p>
                </div>
                <div className="space-y-2">
                  <Label>Template Messaggio</Label>
                  <textarea
                    className="w-full rounded-md border border-slate-200 bg-transparent px-3 py-2 text-sm min-h-[100px] focus:outline-none focus:ring-2 focus:ring-slate-400"
                    value={businessForm.whatsapp_template || "Ciao {nome_cliente}, ti ricordiamo il tuo appuntamento per {servizio} il {data} alle {ora}. A presto! {nome_attivita}"}
                    onChange={(e) => setBusinessForm({...businessForm, whatsapp_template: e.target.value})}
                    data-testid="whatsapp-template-input"
                  />
                  <div className="text-xs text-slate-500 space-y-1">
                    <p className="font-medium">Variabili disponibili:</p>
                    <div className="flex flex-wrap gap-2">
                      {["{nome_cliente}", "{servizio}", "{data}", "{ora}", "{nome_attivita}"].map(v => (
                        <code key={v} className="bg-slate-100 px-1.5 py-0.5 rounded text-xs">{v}</code>
                      ))}
                    </div>
                  </div>
                </div>
                <div className="p-4 bg-green-50 rounded-lg border border-green-200">
                  <p className="text-sm font-medium text-green-800 mb-1">Anteprima messaggio</p>
                  <p className="text-sm text-green-700">
                    {(businessForm.whatsapp_template || "Ciao {nome_cliente}, ti ricordiamo il tuo appuntamento per {servizio} il {data} alle {ora}. A presto! {nome_attivita}")
                      .replace(/{nome_cliente}/g, "Mario Rossi")
                      .replace(/{servizio}/g, "Taglio + Piega")
                      .replace(/{data}/g, "15 agosto 2026")
                      .replace(/{ora}/g, "10:30")
                      .replace(/{nome_attivita}/g, business?.name || "La tua attività")}
                  </p>
                </div>
                <Button onClick={handleUpdateBusiness} data-testid="save-whatsapp-settings">
                  <Save className="h-4 w-4 mr-2" />Salva Impostazioni WhatsApp
                </Button>
              </CardContent>
            </Card>
            )}

            {/* PUSH - Notifiche Push Firebase */}
            {settingsSubTab === "push" && (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Bell className="h-5 w-5" />
                  Notifiche Push
                </CardTitle>
                <CardDescription>
                  Configura il testo delle notifiche push inviate ai clienti
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Info about Firebase config */}
                {!firebaseServerKey && (
                  <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                    <p className="text-sm text-amber-800">
                      ⚠️ La configurazione Firebase deve essere effettuata dall'Amministratore o SuperAdmin.
                      Contatta il tuo amministratore per abilitare le notifiche push.
                    </p>
                  </div>
                )}
                
                {firebaseServerKey && (
                  <div className="bg-green-50 border border-green-200 rounded-lg p-4 flex items-center gap-2">
                    <CheckCircle className="h-5 w-5 text-green-600" />
                    <span className="text-sm text-green-800">
                      Firebase configurato
                      {(() => {
                        try {
                          const parsed = JSON.parse(firebaseServerKey);
                          return ` - Progetto: ${parsed.project_id}`;
                        } catch { return ''; }
                      })()}
                    </span>
                  </div>
                )}

                {/* Notification Settings */}
                <div className="space-y-4">
                  <Label className="mb-3 block font-semibold">Testo Notifiche</Label>
                  
                  {/* Booking Confirmation */}
                      <div>
                        <Label className="text-sm">Titolo conferma prenotazione</Label>
                        <Input
                          placeholder="Prenotazione Confermata ✅"
                          defaultValue={business?.notification_settings?.booking_confirmation_title || "Prenotazione Confermata ✅"}
                          className="mt-1"
                        />
                      </div>
                      <div>
                        <Label className="text-sm">Testo conferma (usa {"{service_name}"}, {"{booking_date}"})</Label>
                        <Input
                          placeholder="{service_name} - {booking_date}"
                          defaultValue={business?.notification_settings?.booking_confirmation_body || "{service_name} - {booking_date}"}
                          className="mt-1"
                        />
                      </div>
                      
                      {/* Reminder */}
                      <div className="border-t pt-4">
                        <Label className="text-sm font-semibold">Promemoria</Label>
                        <div className="flex items-center gap-3 mt-2">
                          <Checkbox id="push-reminder" defaultChecked />
                          <label htmlFor="push-reminder" className="text-sm">Abilita promemoria</label>
                        </div>
                        <div className="mt-2">
                          <Label className="text-sm">Invia promemoria (ore prima)</Label>
                          <div className="flex flex-wrap gap-2 mt-1">
                            {[24, 12, 6, 2, 1].map(h => (
                              <label key={h} className="flex items-center gap-1 text-sm">
                                <Checkbox defaultChecked={h === 24} />
                                {h}h
                              </label>
                            ))}
                          </div>
                        </div>
                        <div className="mt-2">
                          <Label className="text-sm">Titolo promemoria</Label>
                          <Input
                            placeholder="Promemoria Appuntamento 🔔"
                            defaultValue={business?.notification_settings?.reminder_title || "Promemoria Appuntamento 🔔"}
                            className="mt-1"
                          />
                        </div>
                        <div className="mt-2">
                          <Label className="text-sm">Testo promemoria</Label>
                          <Input
                            placeholder="Domani alle {booking_time}: {service_name}"
                            defaultValue={business?.notification_settings?.reminder_body || "Domani alle {booking_time}: {service_name}"}
                            className="mt-1"
                          />
                        </div>
                      </div>
                      
                      <Button className="w-full mt-4" onClick={() => toast.success("Impostazioni notifiche salvate!")}>
                        <Save className="h-4 w-4 mr-2" />
                        Salva Impostazioni Notifiche
                      </Button>
                    </div>
              </CardContent>
            </Card>
            )}
          </TabsContent>
        </Tabs>

        {/* Booking Detail Dialog */}
        <Dialog open={bookingDetailOpen} onOpenChange={setBookingDetailOpen}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <CalendarDays className="h-5 w-5" />
                Dettagli Prenotazione
              </DialogTitle>
            </DialogHeader>
            
            {selectedBooking && !editingBooking && (
              <div className="space-y-4">
                {/* Customer Info */}
                <div className="bg-slate-50 p-4 rounded-lg space-y-2">
                  <h4 className="font-semibold flex items-center gap-2">
                    <UserCircle className="h-4 w-4" /> Cliente
                  </h4>
                  <p className="text-lg font-medium">{selectedBooking.customer_name}</p>
                  {selectedBooking.customer_email && (
                    <p className="text-sm flex items-center gap-2">
                      <Mail className="h-3 w-3 text-slate-400" /> {selectedBooking.customer_email}
                    </p>
                  )}
                  {selectedBooking.customer_phone && (
                    <p className="text-sm flex items-center gap-2">
                      <Phone className="h-3 w-3 text-slate-400" /> {selectedBooking.customer_phone}
                    </p>
                  )}
                </div>

                {/* Booking Info */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm text-slate-500">Data e Ora</p>
                    <p className="font-medium">{formatDateTime(selectedBooking.start_datetime)}</p>
                  </div>
                  <div>
                    <p className="text-sm text-slate-500">Stato</p>
                    <Badge variant={
                      selectedBooking.status === "confirmed" ? "default" : 
                      selectedBooking.status === "pending" ? "secondary" : 
                      selectedBooking.status === "completed" ? "outline" : "destructive"
                    }>
                      {selectedBooking.status === "confirmed" ? "Confermato" : 
                       selectedBooking.status === "pending" ? "In attesa" : 
                       selectedBooking.status === "completed" ? "Completato" : "Annullato"}
                    </Badge>
                  </div>
                </div>

                {/* Services */}
                <div>
                  <p className="text-sm text-slate-500 mb-2">Servizi</p>
                  <div className="space-y-1">
                    {(selectedBooking.services || selectedBooking.services_details || []).map((s, i) => (
                      <div key={i} className="flex justify-between text-sm p-2 bg-slate-50 rounded">
                        <span>{s.name}</span>
                        <span className="font-medium">€{s.price?.toFixed(2)}</span>
                      </div>
                    ))}
                  </div>
                  {selectedBooking.total_price > 0 && (
                    <div className="flex justify-between mt-2 pt-2 border-t font-semibold">
                      <span>Totale</span>
                      <span className="text-green-600">€{selectedBooking.total_price?.toFixed(2)}</span>
                    </div>
                  )}
                </div>

                {/* Notes */}
                {selectedBooking.notes && (
                  <div>
                    <p className="text-sm text-slate-500 mb-1 flex items-center gap-1">
                      <FileText className="h-3 w-3" /> Note
                    </p>
                    <p className="text-sm bg-yellow-50 p-2 rounded">{selectedBooking.notes}</p>
                  </div>
                )}

                <DialogFooter className="flex justify-between">
                  <Button variant="destructive" onClick={handleDeleteBooking}>
                    <Trash2 className="h-4 w-4 mr-2" /> Elimina
                  </Button>
                  <Button onClick={() => setEditingBooking(true)}>
                    <Pencil className="h-4 w-4 mr-2" /> Modifica
                  </Button>
                </DialogFooter>
              </div>
            )}

            {selectedBooking && editingBooking && (
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Nome Cliente</Label>
                    <Input value={bookingEditForm.customer_name} onChange={(e) => setBookingEditForm({...bookingEditForm, customer_name: e.target.value})} />
                  </div>
                  <div className="space-y-2">
                    <Label>Stato</Label>
                    <Select value={bookingEditForm.status} onValueChange={(v) => setBookingEditForm({...bookingEditForm, status: v})}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="confirmed">Confermato</SelectItem>
                        <SelectItem value="pending">In attesa</SelectItem>
                        <SelectItem value="completed">Completato</SelectItem>
                        <SelectItem value="cancelled">Annullato</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Email</Label>
                    <Input type="email" value={bookingEditForm.customer_email} onChange={(e) => setBookingEditForm({...bookingEditForm, customer_email: e.target.value})} />
                  </div>
                  <div className="space-y-2">
                    <Label>Telefono</Label>
                    <Input value={bookingEditForm.customer_phone} onChange={(e) => setBookingEditForm({...bookingEditForm, customer_phone: e.target.value})} />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Data</Label>
                    <Input type="date" value={bookingEditForm.start_datetime} onChange={(e) => setBookingEditForm({...bookingEditForm, start_datetime: e.target.value})} />
                  </div>
                  <div className="space-y-2">
                    <Label>Ora</Label>
                    <Input type="time" value={bookingEditForm.start_time} onChange={(e) => setBookingEditForm({...bookingEditForm, start_time: e.target.value})} />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label>Note</Label>
                  <Textarea value={bookingEditForm.notes} onChange={(e) => setBookingEditForm({...bookingEditForm, notes: e.target.value})} rows={2} />
                </div>
                <DialogFooter>
                  <Button variant="outline" onClick={() => setEditingBooking(false)}>Annulla</Button>
                  <Button onClick={handleUpdateBooking}>Salva Modifiche</Button>
                </DialogFooter>
              </div>
            )}
          </DialogContent>
        </Dialog>

        {/* Delete Confirmation Dialog */}
        <Dialog open={deleteConfirmOpen} onOpenChange={setDeleteConfirmOpen}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 text-red-600">
                <Trash2 className="h-5 w-5" />
                Conferma Eliminazione
              </DialogTitle>
            </DialogHeader>
            <div className="py-4">
              <p className="text-slate-600">
                Sei sicuro di voler eliminare <span className="font-semibold text-slate-900">{deleteTarget.name}</span>?
              </p>
              <p className="text-sm text-red-500 mt-2">
                Questa azione non può essere annullata.
              </p>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setDeleteConfirmOpen(false)} disabled={deleting}>
                Annulla
              </Button>
              <Button 
                variant="destructive" 
                onClick={confirmDelete} 
                disabled={deleting}
                data-testid="confirm-delete-btn"
              >
                {deleting ? (
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                ) : (
                  <Trash2 className="h-4 w-4 mr-2" />
                )}
                Elimina
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
};

export default BusinessOwnerDashboard;
